import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, Subscription, combineLatest } from 'rxjs';
import { map, filter, distinctUntilChanged, take } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { UserService } from './user.service';
import { SpotifyService } from './spotify.service';
import { PlayHistoryService } from './play-history.service';
import { BackendStorageService, PlaybackPosition } from './backend-storage.service';
import {
  PlaybackState,
  WebPlaybackDevice,
  WebPlaybackError,
  SpotifyPlayer,
  PlayerOptions,
  SpotifyUser,
  SpotifyDevice,
  DevicesResponse,
  CrossDevicePlaybackState,
  WebPlaybackTrack
} from '../../shared/models/spotify.models';
import { environment } from '../../../environments/environment';
import { PlaybackJournal } from './playback-journal';

// Forensic diagnostics consumed by the bug-report capture code.
export interface PlaybackRecoveryDiagnostics {
  lastPlayerStateChangedAt: number | null;  // epoch ms of last SDK player_state_changed event; null = never
  lastHealthCheckAt: number | null;         // epoch ms of last health evaluation; null = never
  lastSuccessfulPollAt: number | null;      // epoch ms of last cross-device poll that got a usable response (200 with/without item, or 204); null = never
  lastPolledPlayerState: PolledPlayerSnapshot | null; // what the Web API said on the last successful poll
  lastCommand: CommandDiagnostics | null;   // last user playback command and whether the SDK echoed it
  intendedPlaying: boolean;                 // the user-intent flag the health check runs against
  healthStrikes: number;                    // consecutive unhealthy polls so far
  recoveryHold: string | null;              // non-null = health recovery held (e.g. bug-report dialog open)
  healthVerdicts: HealthVerdict[];          // ring of recent health evaluations (oldest first)
  pollHistory: PolledPlayerSnapshot[];      // ring of recent poll results (oldest first)
  stallEpisodes: StallEpisode[];            // ring of SDK-detected mid-track stalls (oldest first)
}

// One mid-track stall seen from the SDK side: an unprompted player_state_changed
// at a frozen position (the audio buffer ran dry). Short ones heal on their
// own; long ones are confirmed by a poll and recovered.
export interface StallEpisode {
  at: number;                    // epoch ms of the unprompted SDK event
  trackId: string | null;
  positionMs: number;
  outcome: 'open' | 'self-healed' | 'recovered' | 'cleared';
  resolvedAfterMs: number | null;
}

// Snapshot of the Web API's view of playback from the cross-device poll —
// the server-truth input to the health check, contrasted with the SDK's view.
export interface PolledPlayerSnapshot {
  at: number;                    // epoch ms the poll response was read
  requestedAt: number;           // epoch ms the poll request was sent (progress windows use this)
  isPlaying: boolean;
  progressMs: number;
  trackId: string | null;        // currently-playing track per the Web API
  deviceId: string | null;       // active device per the Web API
  isWebSDKActive: boolean;       // active device === our SDK device
}

// Every health evaluation records its outcome here (silent exits included) —
// the 2026-07-11 forensics showed no-log exits are exactly where evidence dies.
export interface HealthVerdict {
  at: number;                    // epoch ms
  verdict: string;
  isPlaying?: boolean;
  progressMs?: number;
  trackId?: string | null;
}

export interface CommandDiagnostics {
  command: string;               // 'resume' | 'pause' | 'toggle' | 'next' | 'previous' | 'seek' | 'play'
  issuedAt: number;              // epoch ms
  echoedAt: number | null;       // epoch ms of first player_state_changed after the command; null = never echoed
}

export type LivePlayerProbeOutcome = 'no-player' | 'state' | 'null-state' | 'timeout-or-error';

export interface LivePlayerProbeResult {
  outcome: LivePlayerProbeOutcome;
  state: PlaybackState | null;              // non-null only when outcome === 'state'
  elapsedMs: number;                        // wall-clock ms the probe took (0 when outcome === 'no-player')
}

@Injectable({
  providedIn: 'root'
})
export class PlaybackService {
  private player: SpotifyPlayer | null = null;
  private deviceId: string | null = null;
  public sdkUnavailable = false;
  
  // State subjects
  private isReadySubject = new BehaviorSubject<boolean>(false);
  private playbackStateSubject = new BehaviorSubject<PlaybackState | null>(null);
  private deviceSubject = new BehaviorSubject<WebPlaybackDevice | null>(null);
  private errorSubject = new BehaviorSubject<WebPlaybackError | null>(null);
  private isInitializingSubject = new BehaviorSubject<boolean>(false);
  private isPremiumSubject = new BehaviorSubject<boolean>(false);

  // Cross-device polling state
  private crossDeviceStateSubject = new BehaviorSubject<CrossDevicePlaybackState | null>(null);
  private currentPlaylistContextIdSubject = new BehaviorSubject<string | null>(null);
  private crossDevicePollingInterval: any = null;
  private consecutiveErrors = 0;
  private maxConsecutiveErrors = 3;
  private errorRetryTimeout: any = null;
  private pollingAuthRecheckInFlight = false;
  private pollingAuthRearmSubscription: Subscription | null = null;

  // Play tracking
  private lastTrackedTrack: string | null = null;
  private stateChangeTimeout: any = null;

  // Progress tracking for recording when track ends/changes
  private lastKnownPosition: number = 0;
  private lastTrackedTrackData: {
    id: string;
    uri: string;
    name: string;
    artistNames: string[];
    albumName: string;
    durationMs: number;
    contextType?: 'playlist' | 'album' | 'artist' | 'track';
    contextUri?: string;
    playlistId?: string;
    albumImage?: string;
  } | null = null;

  // Resume-position tracking: latest known {playlist, track, position},
  // persisted periodically and on unload so Continue Playing can resume mid-song.
  private latestPosition: PlaybackPosition | null = null;
  private latestPositionAt = 0; // epoch ms latestPosition was last updated
  private lastSavedPositionKey: string | null = null;
  private positionFlushInterval: any = null;
  private readonly POSITION_FLUSH_INTERVAL_MS = 15000;
  private unloadHandlersInstalled = false;

  // SDK reconnection: the Web Playback SDK can drop its connection when the
  // token expires (authentication_error) or the network changes (wifi -> mobile,
  // device wakes from sleep). We reconnect the existing player so it re-requests
  // a fresh token via getOAuthToken, rather than forcing the user to hard-refresh.
  private isReconnecting = false;
  private reconnectAttempts = 0;
  private reconnectRetryTimer: ReturnType<typeof setTimeout> | null = null;
  private lastReconnectAttemptAt = 0;
  private readonly RECONNECT_TIMEOUT_MS = 15000;
  private readonly RECONNECT_QUIET_COOLDOWN_MS = 90000;
  private readonly RECONNECT_MAX_BACKOFF_MS = 60000;
  private connectivityHandlersInstalled = false;
  private lastImmediatePollAt = 0; // coalesces connectivity-event poll bursts

  // getOAuthToken cache: the SDK's callback must ALWAYS be answered, otherwise
  // player.connect() never settles. If a fresh fetch fails TRANSIENTLY we fall
  // back to the last good token, bounded by its real expiry (from /auth/token)
  // minus a safety margin. A 401 is a genuine re-auth — never masked by cache.
  private cachedOAuthToken: string | null = null;
  private cachedOAuthTokenAt = 0;
  private cachedOAuthTokenExpiresAt = 0;
  private readonly TOKEN_CACHE_FALLBACK_TTL_MS = 50 * 60 * 1000; // when backend sends no expiresAt
  private readonly TOKEN_CACHE_SAFETY_MARGIN_MS = 2 * 60 * 1000;

  // ---- Playback health (the single detector) ----
  // intendedPlaying is the ONLY authority on whether music should be playing
  // on this device. User commands set it; SDK events never do — a dying stream
  // emits pauses and phantom "playing" states that don't reflect the user's
  // wish (both observed in the field, Sep 2-3 bug reports).
  private intendedPlaying = false;
  private lastHealthCheckAt: number | null = null;
  private previousHealthPoll: PolledPlayerSnapshot | null = null; // the poll before lastPolledPlayerState, for progress-movement comparison
  private healthStrikes = 0;
  private recoveryHoldReason: string | null = null; // non-null = observe but never strike/recover (e.g. bug-report dialog open)
  private readonly HEALTH_STRIKES_TO_RECOVER = 2;   // consecutive unhealthy polls (~10s at the 5s playing poll interval)
  private commandGraceUntil = 0;                    // polls read during this window reflect pre-command state — no strikes
  private readonly COMMAND_GRACE_MS = 10000;
  private readonly RESTART_GRACE_MS = 5000;         // after a same-device restart: buffering time before re-checking
  private readonly PROBE_TIMEOUT_MS = 3000;
  private readonly MIN_PROGRESS_WINDOW_MS = 3000;   // polls closer than this are too jittery to judge progress
  private healthVerdicts: HealthVerdict[] = [];
  private pollHistory: PolledPlayerSnapshot[] = [];
  private readonly FORENSIC_RING_SIZE = 30;

  // Recovery escalation within one unhealthy episode (reset by a healthy poll):
  // 1st = re-issue play on the same device (~1s, proven by manual presses in
  // the Sep 3 / Sep 9 reports), 2nd = reconnect the player, 3rd+ = brand-new
  // Player instance (fresh device id — a reconnect can keep a dead one).
  private recoveryEpisodeStep = 0;
  private lastRecoveryStepAt = 0;
  private recoveryInFlight = false;                 // a step-1 restart command is still on its way
  private currentRecoveryReason: string | null = null;
  private recoveryTimestamps: number[] = [];        // recent recovery steps, for the rolling budget
  private readonly MAX_RECOVERY_STEPS = 3;
  private readonly RECOVERY_STEP_BACKOFF_MS = [0, 10000, 20000]; // min gap before step 1/2/3
  private readonly RECOVERY_BUDGET = 4;             // steps allowed per window, across episodes
  private readonly RECOVERY_BUDGET_WINDOW_MS = 10 * 60 * 1000;
  private readonly RESTART_COMMAND_TIMEOUT_MS = 15000;
  private readonly MAX_POLL_LATENCY_MS = 2000;      // slower poll responses can't judge progress
  private static readonly WEB_API_START_COMMANDS = new Set(['play', 'play-playlist', 'play-tracks', 'play-album', 'takeover']);
  private externallyPaused = false;                 // intent was cleared by a pause from outside the app
  private needsHardRestart = false;                 // recovery gave up: the next Play must restart via the Web API
  private lastAutomaticActionAt = 0;                // last reconnect / auto-resume / recovery step we issued ourselves
  private lastPlaybackErrorAt = 0;                  // last SDK playback_error
  private lastSdkLoadingAt = 0;                     // last SDK state that reported loading=true
  private readonly RECOVERY_EPISODE_EXPIRY_MS = 90000;
  private readonly OUTSIDE_PAUSE_QUIET_MS = 30000;  // after our own automatic actions, pauses are ambiguous
  private recoverOnFirstUnhealthy = false;          // one-shot: next unhealthy verdict recovers without a 2nd strike
  private sdkLoadingSince: number | null = null;    // SDK stuck in loading while the server claims healthy playback

  // Stall suspect: an unprompted SDK state event mid-track while playing is
  // the only local signal of a buffer running dry. Confirmed by a quick poll.
  private stallSuspect: { at: number; trackId: string; positionMs: number; episode: StallEpisode } | null = null;
  private stallSuspectTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly STALL_SUSPECT_CONFIRM_MS = 6500;
  private stallEpisodes: StallEpisode[] = [];

  // Long-hidden detection: after a night in the background Spotify forgets
  // our device id without telling the SDK.
  private hiddenAt: number | null = null;
  private readonly LONG_HIDDEN_MS = 5 * 60 * 1000;

  // Stale remote detection: previous poll sample of a device that isn't us.
  private lastRemoteSample: { deviceId: string; trackId: string; progressMs: number; at: number } | null = null;

  // Raw context of the last poll that had an item — what takeover resumes.
  private lastPolledContext: { contextUri: string | null; trackUri: string; progressMs: number; durationMs: number } | null = null;

  // Polls read before this moment aren't comparable with later ones for
  // progress (a command, restart or reconnect moved the playhead).
  private progressBaselineAt = 0;

  private staleRemoteLogged = false;

  // Device ids this tab used before a reconnect/recreate. Spotify can keep
  // listing an old one as active; it's still us (and silent), never "the
  // user moved playback to another device".
  private retiredDeviceIds = new Set<string>();

  // Polling cadence currently installed (0 = none)
  private currentPollIntervalMs = 0;

  // Last explicit play request — the auto-resume fallback when there is no
  // SDK state and no saved position yet (Oct 16: play accepted, device dropped).
  private lastPlayRequest: { contextUri: string | null; trackUris: string[] | null; offset: number | { uri: string } | undefined; positionMs: number; at: number } | null = null;

  // One-shot auto-resume snapshot, armed before reconnect teardown
  private pendingAutoResume: { contextUri: string | null; trackUri: string | null; offsetIndex?: number; trackUris?: string[]; positionMs: number; wasPlaying: boolean; capturedAt: number } | null = null;
  private autoResumeCancelled = false; // set by noteUserPlaybackCommand to abort an in-flight auto-resume
  private readonly AUTO_RESUME_MAX_AGE_MS = 5 * 60 * 1000;
  private readonly AUTO_RESUME_RETRY_DELAY_MS = 2500; // single bounded retry after a failed attempt
  private readonly AUTO_RESUME_SETTLE_MS = 500; // matches playPlaylist's post-transfer settle delay

  // Forensic timestamps exposed via getRecoveryDiagnostics()
  private lastPlayerStateChangedAt: number | null = null;   // epoch ms
  private lastSuccessfulPollAt: number | null = null;       // epoch ms
  private lastCommand: CommandDiagnostics | null = null;    // record only — no timers, no escalation
  private lastPolledPlayerState: PolledPlayerSnapshot | null = null; // the Web API's latest view

  // playback_error recovery debounce (error bursts collapse into one attempt)
  private playbackErrorDebounceTimer: ReturnType<typeof setTimeout> | null = null;

  // Mobile autoplay unlock — activateElement() must be called once from a
  // user-initiated command.
  private elementActivated = false;

  // User-facing notices from automatic recovery (shown by the floating player)
  private playbackNoticeSubject = new Subject<string>();
  public playbackNotice$ = this.playbackNoticeSubject.asObservable();

  // Public observables
  public isReady$ = this.isReadySubject.asObservable();
  public playbackState$ = this.playbackStateSubject.asObservable();
  public device$ = this.deviceSubject.asObservable();
  public error$ = this.errorSubject.asObservable();
  public isInitializing$ = this.isInitializingSubject.asObservable();
  public isPremium$ = this.isPremiumSubject.asObservable();
  public crossDeviceState$ = this.crossDeviceStateSubject.asObservable();
  public currentPlaylistContextId$ = this.currentPlaylistContextIdSubject.asObservable();

  // Combined playback state - prefers SDK when active, falls back to polled state
  public combinedPlaybackState$: Observable<CrossDevicePlaybackState | null> = combineLatest([
    this.playbackStateSubject,
    this.crossDeviceStateSubject,
    this.isReadySubject
  ]).pipe(
    map(([sdkState, polledState, isSDKReady]) => {
      // If SDK is ready and has state, prefer it
      if (isSDKReady && sdkState && sdkState.track_window?.current_track) {
        const webSDKDeviceId = this.deviceId;
        const isWebSDKActive = polledState?.isWebSDKActive ?? false;

        return {
          currentTrack: sdkState.track_window.current_track,
          isPlaying: !sdkState.paused,
          progress: sdkState.position,
          duration: sdkState.duration,
          isWebSDKActive: isWebSDKActive || isSDKReady,
          shuffleState: sdkState.shuffle,
          repeatMode: sdkState.repeat_mode
        } as CrossDevicePlaybackState;
      }

      // Fall back to polled state
      return polledState;
    }),
    distinctUntilChanged((a, b) => {
      if (a === b) return true;
      if (!a || !b) return false;
      return a.currentTrack?.id === b.currentTrack?.id &&
             a.isPlaying === b.isPlaying &&
             a.isWebSDKActive === b.isWebSDKActive;
    })
  );

  // Convenience observables for components
  public currentTrack$: Observable<WebPlaybackTrack | null> = this.combinedPlaybackState$.pipe(
    map(state => state?.currentTrack || null),
    distinctUntilChanged((a, b) => a?.id === b?.id)
  );

  public isPlayingCombined$: Observable<boolean> = this.combinedPlaybackState$.pipe(
    map(state => state?.isPlaying || false),
    distinctUntilChanged()
  );

  public isWebSDKActive$: Observable<boolean> = this.combinedPlaybackState$.pipe(
    map(state => state?.isWebSDKActive || false),
    distinctUntilChanged()
  );

  constructor(
    private authService: AuthService,
    private userService: UserService,
    private spotifyService: SpotifyService,
    private playHistoryService: PlayHistoryService,
    private backendStorage: BackendStorageService
  ) {
    // Subscribe to auth status changes to check premium when user logs in
    this.authService.authStatus$.subscribe(isAuthenticated => {
      if (isAuthenticated) {
        this.checkPremiumStatus();
        this.startPositionFlushLoop();
      } else {
        this.isPremiumSubject.next(false);
        this.stopPositionFlushLoop();
      }
    });

    this.installUnloadHandlers();
    this.installConnectivityHandlers();
  }

  // When the device comes back online or the tab becomes visible again (e.g.
  // phone unlocked the next morning, or moved from wifi to cellular), the SDK
  // socket is usually dead. If we have a player but it isn't ready, reconnect
  // it — this re-runs getOAuthToken, which now returns a freshly-refreshed token.
  private installConnectivityHandlers(): void {
    if (this.connectivityHandlersInstalled || typeof window === 'undefined') return;
    this.connectivityHandlersInstalled = true;

    window.addEventListener('online', () => {
      console.log('[recovery] trigger: network-online');
      this.maybeRecover('network-online');
      this.triggerImmediatePoll('network-online');
    });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.hiddenAt = Date.now();
        console.log(`[recovery] tab hidden (intent ${this.intendedPlaying ? 'playing' : 'not playing'})`);
        return;
      }
      if (document.visibilityState === 'visible') {
        const hiddenForMs = this.hiddenAt !== null ? Date.now() - this.hiddenAt : 0;
        this.hiddenAt = null;
        console.log(`[recovery] trigger: tab-visible${hiddenForMs ? ` (hidden ${Math.round(hiddenForMs / 1000)}s)` : ''}`);
        this.maybeRecover('tab-visible');
        this.triggerImmediatePoll('tab-visible');
        if (hiddenForMs >= this.LONG_HIDDEN_MS) {
          void this.verifyDeviceRegistered('long-hidden');
        }
      }
    });
    // Interface swaps (wifi <-> cellular) usually kill the SDK socket WITHOUT
    // any offline/online event or 'not_ready' — navigator.connection change is
    // the only signal we get.
    const connection = (navigator as Navigator & {
      connection?: { addEventListener?: (type: string, listener: () => void) => void };
    }).connection;
    if (connection && typeof connection.addEventListener === 'function') {
      connection.addEventListener('change', () => {
        console.log('[recovery] trigger: connection-change');
        this.maybeRecover('connection-change', true);
        this.triggerImmediatePoll('connection-change');
      });
    }
  }

  // Decide whether the SDK needs a reconnect. isReady can be stale after a
  // silent socket death, so instead of trusting it blindly we probe actual
  // liveness via getCurrentState. force=true treats an ambiguous probe result
  // (null state) as dead too — used when the caller has independent evidence
  // (e.g. a network interface change).
  private maybeRecover(reason: string, force = false): void {
    if (this.sdkUnavailable) return;
    if (!this.player) {
      // A failed recreate leaves no Player at all — rebuild it if music should be on.
      if (this.intendedPlaying) {
        console.log(`[recovery] maybeRecover(${reason}): no player while intent is playing, rebuilding`);
        this.forceReconnect(reason);
      }
      return;
    }

    if (!this.isReadySubject.value) {
      console.log(`[recovery] maybeRecover(${reason}): player not ready, reconnecting`);
      this.forceReconnect(reason);
      return;
    }

    this.probePlayerLiveness(reason, force);
  }

  private async probePlayerLiveness(reason: string, force: boolean): Promise<void> {
    if (!this.player) return;
    const believedActive = this.intendedPlaying;
    try {
      const state = await this.withTimeout(
        this.player.getCurrentState(), this.PROBE_TIMEOUT_MS, 'getCurrentState probe'
      );
      // null state is NORMAL for a connected-but-not-active device (user
      // browsing, or playing elsewhere) — only treat it as death when we
      // believed SDK playback was active, or when the caller forced it with
      // independent evidence of socket death (e.g. an interface swap).
      if (state === null && (believedActive || force)) {
        console.log(`[recovery] probe(${reason}): null state treated as dead (${believedActive ? 'was playing' : 'forced by caller'})`);
        this.forceReconnect(reason);
      } else {
        console.log(`[recovery] probe(${reason}): player alive (state ${state ? 'present' : 'null, idle'})`);
      }
    } catch (error) {
      console.log(`[recovery] probe(${reason}): getCurrentState timed out, treating player as dead`, error);
      this.forceReconnect(reason);
    }
  }

  // After a long time hidden (phone asleep overnight) Spotify may have dropped
  // our device id while the SDK still believes it is connected — getCurrentState
  // answers from a local cache, so the liveness probe can't tell. Ask Spotify.
  private async verifyDeviceRegistered(reason: string): Promise<void> {
    if (this.sdkUnavailable || !this.player || !this.deviceId) return;
    if (this.isReconnecting || this.isInitializingSubject.value) return;
    // Audibly playing here right now — the device list may just lag; leave it alone.
    const lastVerdict = this.healthVerdicts[this.healthVerdicts.length - 1]?.verdict;
    if (this.lastPolledPlayerState?.isWebSDKActive && this.lastPolledPlayerState.isPlaying &&
        (lastVerdict === 'healthy' || lastVerdict === 'inconclusive')) {
      console.log(`[recovery] device check (${reason}): skipped, playing healthily here`);
      return;
    }
    const deviceId = this.deviceId;
    const listed = await this.isOurDeviceListed();
    if (this.deviceId !== deviceId || this.isReconnecting) return; // changed meanwhile
    if (listed === null) {
      console.log(`[recovery] device check (${reason}): inconclusive (empty list or lookup failed)`);
    } else if (listed) {
      console.log(`[recovery] device check (${reason}): our device is still registered`);
    } else {
      console.log(`[recovery] device check (${reason}): device ${deviceId} is gone from Spotify — recreating the player`);
      void this.recreatePlayer(reason);
    }
  }

  // true/false = whether Spotify lists our current device; null = unknown
  // (lookup failed or an empty list, which the field data shows can be transient).
  private async isOurDeviceListed(): Promise<boolean | null> {
    const deviceId = this.deviceId;
    if (!deviceId) return null;
    try {
      const result = await this.withTimeout(this.getAvailableDevices(), 5000, 'device list');
      const devices = result?.devices ?? [];
      const listed = devices.some(device => device.id === deviceId);
      // Which devices Spotify knows matters when it 404s a device that just reported 'ready'
      console.log(`[devices] ours=${deviceId.slice(0, 8)} ${listed ? 'listed' : 'NOT listed'}; Spotify lists ` +
        `[${devices.map(device => `${device.id?.slice(0, 8)}${device.is_active ? '*' : ''} ${device.name}`).join(', ')}]`);
      if (devices.length === 0) return null;
      return listed;
    } catch (error) {
      console.log('[devices] device list lookup failed', error);
      return null;
    }
  }

  // Throw the Player instance away and build a new one — the only way to get
  // a fresh device id. Reconnecting the same instance after an overnight
  // expiry either never fires 'ready' (Oct 9 report) or comes back with the
  // device id Spotify has already forgotten (Oct 16 report).
  private async recreatePlayer(reason: string, opts: { skipAutoResumeCapture?: boolean } = {}): Promise<boolean> {
    if (this.sdkUnavailable) return false;
    if (this.isReconnecting || this.isInitializingSubject.value) {
      return this.waitForPlayerReady();
    }
    console.log(`[recovery] recreating player with a fresh device (${reason})`);
    this.cancelReconnectRetry();
    if (!opts.skipAutoResumeCapture) {
      this.capturePendingAutoResume();
    }
    this.clearStallSuspect('cleared');
    const oldPlayer = this.player;
    this.player = null; // listeners of the old instance ignore events from here on
    if (this.deviceId) this.retiredDeviceIds.add(this.deviceId);
    this.deviceId = null;
    this.elementActivated = false;
    this.isReadySubject.next(false);
    this.deviceSubject.next(null);
    this.playbackStateSubject.next(null);
    try { oldPlayer?.disconnect(); } catch { /* ignore */ }

    this.isReconnecting = true;
    this.lastReconnectAttemptAt = Date.now();
    let succeeded = false;
    try {
      succeeded = await this.initializePlayer({ skipPremiumCheck: true });
    } catch (error) {
      console.error(`[recovery] recreating player failed (${reason}):`, error);
    } finally {
      this.isReconnecting = false;
    }
    if (succeeded) {
      console.log(`[recovery] fresh player ready (${reason})`);
      this.tryActivateNewPlayerElement();
    } else {
      this.scheduleReconnectRetry(reason);
    }
    return succeeded;
  }

  // Make sure the local player is connected, without ever spawning a second
  // Player when one already exists.
  private async ensurePlayerReady(reason: string): Promise<boolean> {
    if (this.isReadySubject.value && this.deviceId) return true;
    if (this.sdkUnavailable) return false;
    if (this.isReconnecting || this.isInitializingSubject.value) return this.waitForPlayerReady();
    if (this.player) return this.reconnectPlayer(reason, { skipAutoResumeCapture: true });
    return this.initializePlayer();
  }

  // Pull whatever Spotify last reported as playing onto this device and start
  // it. Used when a play/pause press targets a device that is dead or isn't
  // us (Oct 13/15 reports: pause buttons for a dead tab, presses did nothing).
  public async takeOverPlayback(): Promise<void> {
    console.log('[takeover] starting playback on this device');
    const ready = await this.ensurePlayerReady('takeover');
    if (!ready || !this.deviceId) {
      throw new Error('Player not ready');
    }
    this.noteUserPlaybackCommand('takeover', true);
    const attempt = async (): Promise<void> => {
      const polled = this.lastPolledContext;
      const deviceId = this.deviceId!;
      const nearEnd = !!polled && polled.durationMs > 0 && polled.progressMs >= polled.durationMs - 3000;
      if (polled?.contextUri && polled.trackUri && !nearEnd) {
        this.lastPlayRequest = { contextUri: polled.contextUri, trackUris: null, offset: { uri: polled.trackUri }, positionMs: polled.progressMs, at: Date.now() };
        console.log(`[takeover] resuming ${polled.contextUri} at ${polled.trackUri} @ ${polled.progressMs}ms`);
        await this.spotifyService.startPlayback(deviceId, polled.contextUri, undefined, { uri: polled.trackUri }, polled.progressMs).toPromise();
      } else {
        // Unknown context, or the dead device is parked at the end of a track:
        // move Spotify's own session here and let it carry on from there.
        console.log(`[takeover] ${nearEnd ? 'track was at its end' : 'no known context'} — transferring Spotify's session here`);
        await this.transferPlaybackHere(true);
      }
    };
    try {
      await attempt();
    } catch (error) {
      if (!this.isDeviceNotFoundError(error)) throw error;
      // Spotify also answers 404 for a context it won't play (e.g. a deleted
      // playlist) — only rebuild the player when our device is really gone.
      if (await this.isOurDeviceListed() === true) {
        console.warn('[takeover] 404 but our device is registered — the context was rejected; moving Spotify\'s session here instead');
        await this.transferPlaybackHere(true);
        return;
      }
      console.warn('[takeover] got 404 — stale device id, recreating player and retrying once');
      const recovered = await this.recreatePlayer('takeover-404', { skipAutoResumeCapture: true });
      if (!recovered || !this.deviceId) throw error;
      await attempt();
    }
  }

  // Reconnect now. The attempt counter only restarts when no backoff retry is
  // pending AND the last attempt wasn't moments ago — event-driven triggers may
  // attempt immediately, but they must not collapse the backoff into a storm.
  private forceReconnect(reason: string, opts: { skipAutoResumeCapture?: boolean } = {}): void {
    if (!this.reconnectRetryTimer && Date.now() - this.lastReconnectAttemptAt > 10000) {
      this.reconnectAttempts = 0;
    }
    void this.reconnectPlayer(reason, opts);
  }

  // Reconnect the existing player (preferred over re-initializing, which would
  // spawn a duplicate device). Failures self-rearm with exponential backoff —
  // no state here can permanently block recovery: connect() is bounded by a
  // timeout, isReconnecting always clears in finally, and the attempt counter
  // resets on 'ready' and after a quiet cooldown.
  private async reconnectPlayer(reason: string, opts: { skipAutoResumeCapture?: boolean } = {}): Promise<boolean> {
    if (this.sdkUnavailable) return false;
    if (this.isReconnecting || this.isInitializingSubject.value) return false;

    const now = Date.now();
    if (this.reconnectAttempts > 0 && now - this.lastReconnectAttemptAt > this.RECONNECT_QUIET_COOLDOWN_MS) {
      console.log('[recovery] reconnect backoff rearmed after quiet cooldown');
      this.reconnectAttempts = 0;
    }
    this.lastReconnectAttemptAt = now;
    this.cancelReconnectRetry(); // this attempt supersedes any scheduled one

    this.isReconnecting = true;
    this.reconnectAttempts++;
    console.log(`[recovery] reconnect attempt ${this.reconnectAttempts} (${reason})`);

    let succeeded = false;
    try {
      if (!this.player) {
        succeeded = await this.initializePlayer({ skipPremiumCheck: true });
        if (succeeded) this.tryActivateNewPlayerElement();
        return succeeded;
      }
      // Snapshot playback intent BEFORE teardown so the ready-handler
      // can auto-restore it — this is the automated Continue Playing path.
      // Skipped when the caller is about to issue its own playback command
      // (stale-device retry) or the user explicitly wanted silence.
      if (!opts.skipAutoResumeCapture) {
        this.capturePendingAutoResume();
      }
      // Clear stale device state so waitForDeviceReady waits for a fresh 'ready'
      // ('not_ready' does not clear deviceId on its own). Also drop the stale
      // playback snapshot — believedActive must not read a pre-death state.
      if (this.deviceId) this.retiredDeviceIds.add(this.deviceId);
      this.deviceId = null;
      this.isReadySubject.next(false);
      this.playbackStateSubject.next(null);

      try { this.player.disconnect(); } catch { /* ignore */ }
      // connect() can hang forever if the SDK's getOAuthToken never resolves —
      // bound it so isReconnecting can never latch.
      const connected = await this.withTimeout(
        this.player.connect(), this.RECONNECT_TIMEOUT_MS, 'player.connect()'
      );
      if (connected) {
        succeeded = await this.waitForDeviceReady();
      }
    } catch (error) {
      console.error(`[recovery] reconnect attempt ${this.reconnectAttempts} failed (${reason}):`, error);
    } finally {
      this.isReconnecting = false;
      if (succeeded) {
        console.log(`[recovery] reconnect succeeded (${reason})`);
        this.cancelReconnectRetry();
      } else {
        this.scheduleReconnectRetry(reason);
      }
    }
    return succeeded;
  }

  // Self-rearming backoff: 5s, 10s, 20s, 40s, capped at 60s — retries never
  // permanently stop, so no page reload is ever required.
  private scheduleReconnectRetry(reason: string): void {
    if (this.reconnectRetryTimer) return;
    const delay = Math.min(
      5000 * Math.pow(2, Math.max(this.reconnectAttempts - 1, 0)),
      this.RECONNECT_MAX_BACKOFF_MS
    );
    console.log(`[recovery] scheduling reconnect retry in ${Math.round(delay / 1000)}s (${reason})`);
    this.reconnectRetryTimer = setTimeout(() => {
      this.reconnectRetryTimer = null;
      if (this.isReadySubject.value) return; // recovered in the meantime
      void this.reconnectPlayer(`${reason}-retry`);
    }, delay);
  }

  private cancelReconnectRetry(): void {
    if (this.reconnectRetryTimer) {
      clearTimeout(this.reconnectRetryTimer);
      this.reconnectRetryTimer = null;
    }
  }

  // Snapshot what should be playing before reconnect teardown wipes the cached
  // state, so the ready-handler can automatically restore playback. Gated on
  // user intent — never on the SDK's paused flag, which lies after a stream
  // death. Falls back to the persisted resume position when the SDK has no
  // usable state (e.g. play pressed on an idle player).
  private capturePendingAutoResume(): void {
    if (this.pendingAutoResume) return; // first (pre-death) snapshot wins across backoff retries
    if (!this.intendedPlaying) return;  // only restore what the user wants playing
    const snapshot = this.buildResumeSnapshot({ trustServerProgress: this.currentRecoveryReason !== 'sdk-loading-stuck' });
    if (!snapshot) return;
    this.pendingAutoResume = snapshot;
    console.log(`[recovery] auto-resume armed: context ${snapshot.contextUri ?? 'none'}, track ${snapshot.trackUri ?? (snapshot.offsetIndex !== undefined ? `#${snapshot.offsetIndex}` : 'none')}, position ${snapshot.positionMs}ms`);
  }

  // What should be playing right now, best evidence first: the SDK's last
  // state, else the user's own last play request (when newer than the saved
  // position — that may describe yesterday's dead device), else the saved position.
  private buildResumeSnapshot(opts: { trustServerProgress?: boolean } = {}): NonNullable<typeof this.pendingAutoResume> | null {
    const trustServerProgress = opts.trustServerProgress ?? true;
    const now = Date.now();
    const state = this.playbackStateSubject.value;
    if (state && (state.context?.uri || state.track_window?.current_track?.uri)) {
      const trackId = state.track_window?.current_track?.id ?? null;
      return {
        contextUri: state.context?.uri ?? null,
        trackUri: state.track_window?.current_track?.uri ?? null,
        positionMs: this.bestKnownPositionMs(trackId, state.position, trustServerProgress),
        wasPlaying: true,
        capturedAt: now
      };
    }
    const request = this.lastPlayRequest;
    const requestIsFresh = !!request && now - request.at < this.AUTO_RESUME_MAX_AGE_MS;
    if (request && requestIsFresh && request.at >= this.latestPositionAt) {
      return {
        contextUri: request.contextUri,
        trackUri: typeof request.offset === 'object' ? request.offset.uri : null,
        offsetIndex: typeof request.offset === 'number' ? request.offset : undefined,
        trackUris: request.trackUris ?? undefined,
        positionMs: request.positionMs,
        wasPlaying: true,
        capturedAt: now
      };
    }
    if (this.latestPosition) {
      return {
        contextUri: `spotify:playlist:${this.latestPosition.playlistId}`,
        trackUri: this.latestPosition.trackUri ?? null,
        positionMs: this.latestPosition.positionMs ?? 0,
        wasPlaying: true,
        capturedAt: now
      };
    }
    return null;
  }

  // Every source here is a lower bound of the true position (the SDK event
  // position is never counted forward), so the max is the safest restart
  // point: it never skips ahead, and never jumps back to the track's start
  // just because the SDK has been quiet since the track began.
  // (Not when the SDK was stuck loading: then the server's progress is
  // extrapolated past audio that was never heard.)
  private bestKnownPositionMs(trackId: string | null, sdkPositionMs: number, trustServerProgress = true): number {
    let best = Number.isFinite(sdkPositionMs) && sdkPositionMs > 0 ? sdkPositionMs : 0;
    if (this.stallSuspect && this.stallSuspect.trackId === trackId) {
      best = Math.max(best, this.stallSuspect.positionMs);
    }
    const polled = this.lastPolledPlayerState;
    if (trustServerProgress && polled && trackId && polled.trackId === trackId && Date.now() - polled.at < 60000) {
      best = Math.max(best, polled.progressMs);
    }
    return Math.floor(best);
  }

  private startPlaybackFromSnapshot(deviceId: string, snapshot: NonNullable<typeof this.pendingAutoResume>): Promise<void | undefined> {
    const offset = snapshot.trackUri ? { uri: snapshot.trackUri } : snapshot.offsetIndex;
    const uris = snapshot.contextUri ? undefined : (snapshot.trackUris ?? (snapshot.trackUri ? [snapshot.trackUri] : undefined));
    return this.spotifyService.startPlayback(
      deviceId,
      snapshot.contextUri ?? undefined,
      uris,
      snapshot.contextUri || snapshot.trackUris ? offset : undefined,
      snapshot.positionMs
    ).toPromise();
  }

  // One-shot consumption in the 'ready' handler after a reconnect.
  private maybeAutoResumeAfterReconnect(): void {
    const snapshot = this.pendingAutoResume;
    this.pendingAutoResume = null; // one-shot: consumed on first ready, can never fire twice
    if (!snapshot || !snapshot.wasPlaying) return;
    if (Date.now() - snapshot.capturedAt > this.AUTO_RESUME_MAX_AGE_MS) {
      console.log('[recovery] auto-resume snapshot expired, not resuming');
      return;
    }
    if (!snapshot.contextUri && !snapshot.trackUri && !snapshot.trackUris) return;
    this.autoResumeCancelled = false;
    void this.performAutoResume(snapshot); // fire-and-forget; ready-handler must stay synchronous
  }

  private async performAutoResume(snapshot: NonNullable<typeof this.pendingAutoResume>): Promise<void> {
    console.log(`[recovery] auto-resume: restoring playback on reconnected device (context ${snapshot.contextUri ?? 'none'}, track ${snapshot.trackUri ?? 'none'}, position ${snapshot.positionMs}ms)`);
    // One immediate attempt plus a single bounded retry — a freshly registered
    // Web SDK device can 404 ('Device not found') for a moment, and the network
    // is by definition just recovering. Still one-shot per episode; a user
    // command cancels via autoResumeCancelled.
    for (let attempt = 1; attempt <= 2; attempt++) {
      if (this.autoResumeCancelled || !this.deviceId) return;
      // Not a user command (no noteUserPlaybackCommand/activateElement), but grant
      // the same health-check grace through startup buffering a user command gets.
      this.markPlaybackRestart(this.COMMAND_GRACE_MS);
      try {
        // Claim the freshly registered device first (without playing the old
        // context), then give the transfer a moment to settle.
        await this.transferPlaybackHere(false);
        await new Promise(resolve => setTimeout(resolve, this.AUTO_RESUME_SETTLE_MS));
        if (this.autoResumeCancelled || !this.deviceId) return;
        await this.startPlaybackFromSnapshot(this.deviceId, snapshot);
        if (!this.intendedPlaying) {
          // The user paused while this was in flight — their pause wins.
          console.log('[recovery] auto-resume landed after a user pause — pausing again');
          await this.player?.pause().catch(() => undefined);
          return;
        }
        console.log('[recovery] auto-resume: playback restored');
        return;
      } catch (error) {
        if (attempt === 1 && !this.autoResumeCancelled) {
          console.warn(`[recovery] auto-resume attempt 1 failed, retrying once in ${this.AUTO_RESUME_RETRY_DELAY_MS}ms:`, error);
          await new Promise(resolve => setTimeout(resolve, this.AUTO_RESUME_RETRY_DELAY_MS));
        } else {
          console.error('[recovery] auto-resume failed (Continue Playing remains available):', error);
        }
      }
    }
  }

  // Bound a promise so a hung SDK call can never wedge recovery logic.
  private withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms);
      promise.then(
        value => { clearTimeout(timer); resolve(value); },
        err => { clearTimeout(timer); reject(err); }
      );
    });
  }

  private startPositionFlushLoop(): void {
    if (this.positionFlushInterval) return;
    this.positionFlushInterval = setInterval(() => {
      this.flushPosition();
    }, this.POSITION_FLUSH_INTERVAL_MS);
  }

  private stopPositionFlushLoop(): void {
    if (this.positionFlushInterval) {
      clearInterval(this.positionFlushInterval);
      this.positionFlushInterval = null;
    }
  }

  private positionKey(pos: PlaybackPosition): string {
    // Round to 5s so trivial drift doesn't trigger a save.
    return `${pos.playlistId}|${pos.trackId}|${Math.floor(pos.positionMs / 5000)}`;
  }

  private updateLatestPosition(
    playlistId: string,
    trackId: string,
    trackUri: string | undefined,
    positionMs: number,
    durationMs: number | undefined
  ): void {
    if (!playlistId || !trackId || !Number.isFinite(positionMs) || positionMs < 0) return;
    this.latestPosition = { playlistId, trackId, trackUri, positionMs, durationMs };
    this.latestPositionAt = Date.now();
  }

  private flushPosition(): void {
    const pos = this.latestPosition;
    if (!pos) return;
    const key = this.positionKey(pos);
    if (key === this.lastSavedPositionKey) return;
    this.lastSavedPositionKey = key;
    this.backendStorage.savePlaybackPosition(pos).subscribe({
      error: (err) => {
        console.warn('Failed to save playback position:', err);
        // Allow a retry on next tick.
        this.lastSavedPositionKey = null;
      }
    });
  }

  // Synchronous-ish flush for tab close / hide — uses fetch keepalive so the
  // browser allows the request to outlive the page.
  private flushPositionOnUnload(): void {
    const pos = this.latestPosition;
    if (!pos) return;
    try {
      fetch(`${environment.apiUrl}/user-data/playback-position`, {
        method: 'POST',
        credentials: 'include',
        keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(pos)
      }).catch(() => {});
    } catch {
      // Ignore — best effort.
    }
  }

  private installUnloadHandlers(): void {
    if (this.unloadHandlersInstalled || typeof window === 'undefined') return;
    this.unloadHandlersInstalled = true;
    window.addEventListener('beforeunload', () => this.flushPositionOnUnload());
    window.addEventListener('pagehide', () => this.flushPositionOnUnload());
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.flushPositionOnUnload();
      }
    });
  }

  private async checkPremiumStatus(): Promise<void> {
    try {
      console.log('Checking premium status...');
      const user = await this.userService.getUserProfile().toPromise();
      console.log('User profile received:', user);
      console.log('User product:', user?.product);
      
      const isPremium = user?.product === 'premium';
      console.log('Is premium user:', isPremium);
      
      this.isPremiumSubject.next(isPremium);
    } catch (error) {
      // A network error says nothing about the account — keep the last known
      // value rather than locking a Premium user out of recovery.
      console.error('Failed to check premium status (keeping last known value):', error);
    }
  }

  private isEMESupported(): boolean {
    return typeof navigator.requestMediaKeySystemAccess === 'function';
  }

  public async initializePlayer(opts: { skipPremiumCheck?: boolean } = {}): Promise<boolean> {
    // Check EME support first — Android WebViews don't support it
    if (!this.isEMESupported()) {
      console.warn('EME not supported (likely Android WebView). Skipping Web Playback SDK.');
      this.sdkUnavailable = true;
      return false;
    }

    // First, make sure we have the latest premium status (recovery paths skip
    // the round-trip when Premium is already known)
    if (!opts.skipPremiumCheck || !this.isPremiumSubject.value) {
      await this.checkPremiumStatus();
    }

    if (!this.isPremiumSubject.value) {
      this.errorSubject.next({
        message: 'Spotify Premium is required for playback functionality',
        type: 'account_error'
      });
      return false;
    }

    if (this.isReconnecting && this.player) {
      // A reconnect of the existing Player is in flight — a second Player
      // would register a duplicate device.
      console.log('Player reconnecting, waiting for it instead of creating another...');
      return await this.waitForPlayerReady();
    }

    if (this.isInitializingSubject.value) {
      console.log('Player already initializing, waiting for completion...');
      // Wait for initialization to complete
      return await this.waitForPlayerReady();
    }

    this.isInitializingSubject.next(true);
    this.errorSubject.next(null);

    try {
      // Load Spotify Web Playback SDK if not already loaded
      await this.loadSpotifySDK();
      
      // Create player instance
      await this.createPlayer();
      
      // Connect to Spotify
      const connected = await this.connectPlayer();
      
      if (connected) {
        // Wait for the device to be ready (for the ready event to fire)
        console.log('Waiting for device to be ready...');
        const deviceReady = await this.waitForDeviceReady();
        this.isInitializingSubject.next(false);
        return deviceReady;
      }
      
      this.isInitializingSubject.next(false);
      return false;
    } catch (error) {
      console.error('Failed to initialize player:', error);
      this.isInitializingSubject.next(false);
      this.errorSubject.next({
        message: error instanceof Error ? error.message : 'Failed to initialize player',
        type: 'initialization_error'
      });
      return false;
    }
  }

  private loadSpotifySDK(): Promise<void> {
    return new Promise((resolve, reject) => {
      console.log('Loading Spotify Web Playback SDK...');
      
      // Check if SDK is already loaded
      if (window.Spotify) {
        console.log('Spotify SDK already loaded');
        resolve();
        return;
      }

      // Set up the callback for when SDK is ready
      window.onSpotifyWebPlaybackSDKReady = () => {
        console.log('Spotify Web Playback SDK ready callback triggered');
        resolve();
      };

      // Load the SDK script
      console.log('Creating script tag for Spotify SDK...');
      const script = document.createElement('script');
      script.src = 'https://sdk.scdn.co/spotify-player.js';
      script.async = true;
      script.onload = () => console.log('Spotify SDK script loaded');
      script.onerror = () => {
        console.error('Failed to load Spotify Web Playback SDK script');
        reject(new Error('Failed to load Spotify Web Playback SDK'));
      };
      document.head.appendChild(script);
      console.log('Spotify SDK script tag added to document head');
    });
  }

  private async createPlayer(): Promise<void> {
    console.log('Creating Spotify player...');
    
    if (!window.Spotify) {
      throw new Error('Spotify SDK not loaded');
    }

    const playerOptions: PlayerOptions = {
      name: 'Spotify AI Playlist Manager',
      getOAuthToken: (cb) => {
        console.log('Player requesting OAuth token...');
        this.authService.getAccessToken().subscribe({
          next: ({ token, expiresAt }) => {
            console.log('OAuth token received for player:', token ? 'Present' : 'Missing');
            if (token) {
              this.cachedOAuthToken = token;
              this.cachedOAuthTokenAt = Date.now();
              this.cachedOAuthTokenExpiresAt = expiresAt ?? (Date.now() + this.TOKEN_CACHE_FALLBACK_TTL_MS);
            }
            cb(token);
          },
          error: (error: unknown) => {
            console.error('Failed to get access token for player:', error);
            const status = (error as { status?: number }).status;
            if (status === 401) {
              // Genuine re-auth needed — never mask it with a cached token.
              console.log('[recovery] token fetch got 401 — re-auth required, discarding cached token');
              this.cachedOAuthToken = null;
              this.errorSubject.next({
                message: 'Authentication failed',
                type: 'authentication_error'
              });
              cb('');
              return;
            }
            // Transient failure (network / 503 / timeout): fall back to the
            // cached token while it is genuinely still valid.
            const validityLeftMs = this.cachedOAuthTokenExpiresAt - Date.now();
            if (this.cachedOAuthToken && validityLeftMs > this.TOKEN_CACHE_SAFETY_MARGIN_MS) {
              console.log(`[recovery] token fetch failed transiently, using cached token (${Math.round(validityLeftMs / 1000)}s validity left)`);
              cb(this.cachedOAuthToken);
              return;
            }
            // No usable cache. Answer the SDK anyway: an empty token provokes
            // authentication_error, which is a RECOVERABLE path (reconnect).
            // Never returning would hang player.connect() forever.
            console.log('[recovery] token fetch failed, no usable cached token — answering SDK with empty token');
            this.errorSubject.next({
              message: 'Authentication failed',
              type: 'authentication_error'
            });
            cb('');
          }
        });
      },
      volume: 0.5
    };

    console.log('Creating new Spotify.Player instance...');
    this.player = new window.Spotify.Player(playerOptions);
    console.log('Player instance created');

    // Set up event listeners
    this.setupPlayerEventListeners();
    console.log('Player event listeners set up');
  }

  private setupPlayerEventListeners(): void {
    const player = this.player;
    if (!player) return;
    // A recreated player replaces this instance; its late events must not
    // overwrite the new instance's device id or state.
    const isCurrent = (): boolean => this.player === player;

    // Ready event
    player.addListener('ready', ({ device_id }) => {
      if (!isCurrent()) return;
      console.log('Spotify player ready with device ID:', device_id);
      this.lastAutomaticActionAt = Date.now();
      this.deviceId = device_id;
      this.retiredDeviceIds.delete(device_id); // a reconnect can come back with the same id
      this.reconnectAttempts = 0; // healthy connection — reset the retry budget
      this.cancelReconnectRetry();
      this.resetHealthStrikes();
      this.progressBaselineAt = Date.now(); // pre-reconnect polls aren't comparable
      this.isReadySubject.next(true);
      this.syncPollingInterval();
      this.deviceSubject.next({
        device_id,
        name: 'Spotify AI Playlist Manager',
        type: 'Computer',
        volume_percent: 50,
        is_active: true,
        is_private_session: false,
        is_restricted: false,
        supports_volume: true
      });
      this.maybeAutoResumeAfterReconnect();
    });

    // Not ready event
    player.addListener('not_ready', ({ device_id }) => {
      if (!isCurrent()) return;
      console.log('Spotify player not ready with device ID:', device_id);
      this.isReadySubject.next(false);
      this.deviceSubject.next(null);
    });

    // Player state changed (debounced to prevent spam)
    player.addListener('player_state_changed', (state) => {
      if (!isCurrent()) return;
      const previousState = this.playbackStateSubject.value;
      this.lastPlayerStateChangedAt = Date.now(); // forensic timestamp
      if (state?.loading) this.lastSdkLoadingAt = Date.now();
      this.noteCommandEchoed();                   // forensic: the SDK acknowledged the last user command
      this.noteNaturalPlaybackEnd(state);         // end-of-playlist pause is the ONE SDK event allowed to clear intent
      this.noteSdkStateForStalls(previousState, state);
      this.playbackStateSubject.next(state);
      
      // Debounce state change handling to prevent excessive calls
      if (this.stateChangeTimeout) {
        clearTimeout(this.stateChangeTimeout);
      }
      
      this.stateChangeTimeout = setTimeout(() => {
        this.handlePlaybackStateChange(state);
      }, 500); // 500ms debounce
    });

    // Error events
    player.addListener('initialization_error', ({ message }) => {
      if (!isCurrent()) return;
      console.error('Spotify player initialization error:', message);
      this.errorSubject.next({ message, type: 'initialization_error' });
    });

    player.addListener('authentication_error', ({ message }) => {
      if (!isCurrent()) return;
      console.error('Spotify player authentication error:', message);
      this.errorSubject.next({ message, type: 'authentication_error' });
      // Almost always an expired token. /auth/token now refreshes on demand,
      // so reconnecting re-fetches a valid token and restores playback.
      void this.reconnectPlayer('authentication_error');
    });

    player.addListener('account_error', ({ message }) => {
      if (!isCurrent()) return;
      console.error('Spotify player account error:', message);
      this.errorSubject.next({ message, type: 'account_error' });
    });

    player.addListener('playback_error', ({ message }) => {
      if (!isCurrent()) return;
      console.error('Spotify player playback error:', message);
      this.lastPlaybackErrorAt = Date.now();
      this.errorSubject.next({ message, type: 'playback_error' });
      // Debounced corrective action — error bursts collapse into one attempt.
      this.schedulePlaybackErrorRecovery();
    });
  }

  private schedulePlaybackErrorRecovery(): void {
    if (this.playbackErrorDebounceTimer) {
      clearTimeout(this.playbackErrorDebounceTimer);
    }
    this.playbackErrorDebounceTimer = setTimeout(() => {
      this.playbackErrorDebounceTimer = null;
      if (!this.intendedPlaying) {
        console.log('[recovery] playback_error burst settled while idle — no action');
        return;
      }
      if (Date.now() < this.commandGraceUntil) return; // an error from the user's own command must not trigger recovery against them
      console.log('[recovery] playback_error burst settled, recovering');
      this.recoverPlayback('playback-error');
    }, 2000);
  }

  private async connectPlayer(): Promise<boolean> {
    console.log('Connecting player to Spotify...');
    
    if (!this.player) {
      throw new Error('Player not created');
    }

    try {
      const connected = await this.player.connect();
      console.log('Player connection result:', connected);
      
      if (!connected) {
        throw new Error('Failed to connect to Spotify');
      }

      return connected;
    } catch (error) {
      console.error('Error during player connection:', error);
      throw error;
    }
  }

  private waitForDeviceReady(): Promise<boolean> {
    return new Promise((resolve) => {
      const checkInterval = setInterval(() => {
        if (this.deviceId) {
          console.log('Device is ready with ID:', this.deviceId);
          clearInterval(checkInterval);
          clearTimeout(timeoutTimer); // a stale timer logged false "Timeout waiting" lines (Oct 16 report)
          resolve(true);
        }
      }, 100);

      // Timeout after 10 seconds
      const timeoutTimer = setTimeout(() => {
        clearInterval(checkInterval);
        if (!this.deviceId) {
          console.error('Timeout waiting for device to be ready');
          resolve(false);
        }
      }, 10000);
    });
  }

  private waitForPlayerReady(): Promise<boolean> {
    return new Promise((resolve) => {
      const checkInterval = setInterval(() => {
        if (this.isReadySubject.value && this.deviceId) {
          console.log('Player is ready');
          clearInterval(checkInterval);
          clearTimeout(timeoutTimer);
          resolve(true);
        }
      }, 100);

      // Timeout after 10 seconds
      const timeoutTimer = setTimeout(() => {
        clearInterval(checkInterval);
        if (!this.isReadySubject.value || !this.deviceId) {
          console.error('Timeout waiting for player to be ready');
          resolve(false);
        }
      }, 10000);
    });
  }

  public async disconnectPlayer(): Promise<void> {
    this.setIntendedPlaying(false); // a deliberate disconnect ends the intent to play here
    this.resetHealthStrikes();
    this.cancelReconnectRetry();
    this.cancelPendingAutoResume('player disconnected'); // a stale snapshot must never fire on a later ready
    if (this.playbackErrorDebounceTimer) {
      clearTimeout(this.playbackErrorDebounceTimer);
      this.playbackErrorDebounceTimer = null;
    }
    if (this.player) {
      this.player.disconnect();
      this.player = null;
      this.deviceId = null;
      this.elementActivated = false;
      this.isReadySubject.next(false);
      this.deviceSubject.next(null);
      this.playbackStateSubject.next(null);
    }
  }

  // ---- Playback health check (the single detector) ----
  // One rule replaces the old watchdog zoo: while the user wants music playing
  // on this device, the Web API (server truth) must show OUR device playing
  // with progress moving. Two consecutive polls that contradict that mean the
  // stream is dead — recover. SDK-side signals (pause events, frozen or
  // phantom-advancing positions) are symptoms, never intent.

  private resetHealthStrikes(): void {
    this.healthStrikes = 0;
  }

  private noteHealthVerdict(verdict: string, poll?: PolledPlayerSnapshot | null): void {
    const entry: HealthVerdict = { at: Date.now(), verdict };
    if (poll) {
      entry.isPlaying = poll.isPlaying;
      entry.progressMs = poll.progressMs;
      entry.trackId = poll.trackId;
    }
    this.healthVerdicts.push(entry);
    PlaybackJournal.health(verdict, poll
      ? `playing=${poll.isPlaying} pos=${poll.progressMs} track=${poll.trackId} here=${poll.isWebSDKActive} ` +
        `intent=${this.intendedPlaying} strikes=${this.healthStrikes}`
      : undefined);
    if (this.healthVerdicts.length > this.FORENSIC_RING_SIZE) {
      this.healthVerdicts.shift();
    }
  }

  // While held, the health check observes but never strikes or recovers.
  // Used by the bug-report dialog: mic dictation pauses the stream via Android
  // audio focus, and that pause is an artifact of reporting, not the bug.
  // But the user usually opens the dialog BECAUSE the music just died — if
  // playback is already unhealthy, recover first (Oct 15 report never did).
  public holdRecovery(reason: string): void {
    const lastVerdict = this.healthVerdicts[this.healthVerdicts.length - 1]?.verdict ?? '';
    const alreadyUnhealthy = this.intendedPlaying && !this.isReconnecting &&
      (this.healthStrikes > 0 || !!this.stallSuspect || lastVerdict.startsWith('unhealthy'));
    this.recoveryHoldReason = reason;
    this.resetHealthStrikes();
    if (alreadyUnhealthy) {
      console.log(`[health] recovery hold (${reason}) — playback already unhealthy (${lastVerdict || 'stall suspect'}), recovering first`);
      this.recoverPlayback('unhealthy-at-hold');
      return;
    }
    console.log(`[health] recovery held (${reason})`);
  }

  public releaseRecovery(): void {
    if (!this.recoveryHoldReason) return;
    console.log(`[health] recovery released (${this.recoveryHoldReason})`);
    this.recoveryHoldReason = null;
    if (this.intendedPlaying) {
      // A mic-induced pause shouldn't cost another two strikes after closing.
      this.recoverOnFirstUnhealthy = true;
      this.triggerImmediatePoll('recovery-released');
    }
  }

  private endRecoveryEpisode(why: string): void {
    if (this.recoveryEpisodeStep > 0) {
      console.log(`[health] recovery episode over (${why})`);
    }
    this.recoveryEpisodeStep = 0;
    this.currentRecoveryReason = null;
  }

  // An episode whose last step was long ago is over, whatever the verdicts
  // said (slow-data polls come back inconclusive and never reset it).
  private expireStaleRecoveryEpisode(): void {
    if (this.recoveryEpisodeStep > 0 && Date.now() - this.lastRecoveryStepAt > this.RECOVERY_EPISODE_EXPIRY_MS) {
      this.endRecoveryEpisode('expired');
    }
  }

  // Runs once per successful cross-device poll — strikes count DISTINCT polls.
  private evaluatePlaybackHealth(): void {
    this.lastHealthCheckAt = Date.now();
    this.expireStaleRecoveryEpisode();
    if (!this.intendedPlaying) {
      this.noteHealthVerdict('idle');
      this.resetHealthStrikes();
      this.recoveryEpisodeStep = 0;
      this.clearStallSuspect('cleared');
      return;
    }
    if (!this.player || this.sdkUnavailable || !this.isReadySubject.value) { this.noteHealthVerdict('sdk-not-ready'); return; }
    if (this.isReconnecting || this.isInitializingSubject.value) { this.noteHealthVerdict('reconnecting'); return; }
    // Held: e.g. the bug-report dialog is open — mic dictation takes Android
    // audio focus and pauses the stream, which is indistinguishable from a
    // stream death. Recovery must not restart music into an open microphone.
    if (this.recoveryHoldReason) { this.noteHealthVerdict(`held-${this.recoveryHoldReason}`); return; }
    // A recovery command is still on its way — judging now would escalate
    // against our own in-flight fix.
    if (this.recoveryInFlight) { this.noteHealthVerdict('recovery-in-flight'); return; }
    // A poll read during the grace window reflects pre-command state.
    if (Date.now() < this.commandGraceUntil) { this.noteHealthVerdict('command-grace'); return; }

    const current = this.lastPolledPlayerState;
    if (!current) { this.noteHealthVerdict('no-poll-data'); return; }

    // Music running on ANOTHER device means the user moved playback — respect it.
    // (A dead device — including one of our own retired device ids — is
    // recorded as not playing, so it never triggers this.)
    if (current.isPlaying && !current.isWebSDKActive) {
      console.log('[health] playback active on another device — standing down');
      this.setIntendedPlaying(false);
      this.noteHealthVerdict('external-device', current);
      this.resetHealthStrikes();
      this.recoveryEpisodeStep = 0;
      this.clearStallSuspect('cleared');
      return;
    }

    // Progress must keep up with the wall clock. "Moved at all" scored a
    // stream that froze mid-window as healthy (Oct 15: +1.7s over 5.1s).
    // The window is measured between request SEND times; a slow response
    // can't be judged (it would stretch the window and fake a stall).
    let why: string | null = current.isPlaying ? null : 'not-playing';
    let rateInconclusive = false;
    const previous = this.previousHealthPoll;
    const comparable = !!previous && previous.isPlaying && previous.trackId === current.trackId &&
      previous.requestedAt >= this.progressBaselineAt;
    if (!why && comparable) {
      const wallMs = current.requestedAt - previous!.requestedAt;
      const advancedMs = current.progressMs - previous!.progressMs;
      const slowResponse = current.at - current.requestedAt > this.MAX_POLL_LATENCY_MS ||
        previous!.at - previous!.requestedAt > this.MAX_POLL_LATENCY_MS;
      if (wallMs < this.MIN_PROGRESS_WINDOW_MS || slowResponse) {
        rateInconclusive = true;
      } else if (advancedMs >= 0 && advancedMs < wallMs * 0.5) {
        why = advancedMs === 0 ? 'progress-frozen' : 'progress-stalled';
      }
    }

    // An SDK stall suspect is confirmed when the server's progress hasn't
    // moved meaningfully past the position the SDK froze at.
    const suspect = this.stallSuspect;
    const suspectSettled = !!suspect && current.at - suspect.at >= 3000;
    const suspectConfirmed = suspectSettled && current.trackId === suspect!.trackId &&
      (!current.isPlaying || current.progressMs <= suspect!.positionMs + 1000);
    if (!why && suspectConfirmed) {
      why = 'segment-stall';
    }

    if (!why && rateInconclusive) {
      // Can't judge the RATE, but our device playing with progress moving
      // forward means the last recovery worked — end that episode.
      if (current.isWebSDKActive && previous && previous.trackId === current.trackId &&
          current.progressMs > previous.progressMs && this.recoveryEpisodeStep > 0) {
        this.endRecoveryEpisode('progress advancing');
      }
      this.noteHealthVerdict('inconclusive', current); // neither strike nor reset
      return;
    }

    if (!why) {
      this.noteHealthVerdict('healthy', current);
      this.resetHealthStrikes();
      this.endRecoveryEpisode('healthy');
      this.recoverOnFirstUnhealthy = false;
      if (suspectSettled) {
        // Progress well past the suspect position = it never stalled at all.
        const keptUp = current.progressMs > suspect!.positionMs + (current.at - suspect!.at) * 0.8;
        this.clearStallSuspect(keptUp ? 'cleared' : 'self-healed');
      }
      void this.checkSdkLoadingStuck();
      return;
    }

    this.healthStrikes++;
    this.noteHealthVerdict(`unhealthy-${why}`, current);
    // Skip the second strike only with independent evidence: a confirmed SDK
    // stall, or a just-released bug-report hold.
    const immediate = suspectConfirmed || this.recoverOnFirstUnhealthy;
    console.log(`[health] strike ${this.healthStrikes}/${this.HEALTH_STRIKES_TO_RECOVER} (${why}) while intent is playing${immediate ? ' — recovering now' : ''}`);
    if (immediate || this.healthStrikes >= this.HEALTH_STRIKES_TO_RECOVER) {
      this.resetHealthStrikes();
      this.recoverOnFirstUnhealthy = false;
      this.recoverPlayback(why);
    }
  }

  // The server's progress can look healthy while the SDK never actually got
  // audio going (Oct 20: loading=true 11s after play). Probe the SDK itself.
  private async checkSdkLoadingStuck(): Promise<void> {
    if (!this.player || !this.lastPolledPlayerState?.isWebSDKActive) { this.sdkLoadingSince = null; return; }
    try {
      const state = await this.withTimeout(this.player.getCurrentState(), this.PROBE_TIMEOUT_MS, 'loading probe');
      if (!state || !state.loading || state.paused) { this.sdkLoadingSince = null; return; }
      const now = Date.now();
      this.lastSdkLoadingAt = now;
      if (this.sdkLoadingSince === null) { this.sdkLoadingSince = now; return; }
      const stuckMs = now - this.sdkLoadingSince;
      if (stuckMs >= 8000 && this.intendedPlaying && !this.recoveryHoldReason && !this.isReconnecting &&
          !this.recoveryInFlight && now >= this.commandGraceUntil) {
        console.log(`[health] SDK stuck loading for ${stuckMs}ms while the server reports playing — recovering`);
        this.sdkLoadingSince = null;
        this.noteHealthVerdict('unhealthy-sdk-loading-stuck');
        this.recoverPlayback('sdk-loading-stuck');
      }
    } catch {
      // A probe failure isn't evidence of a loading stall.
    }
  }

  // Recovery escalates within an unhealthy episode; a healthy poll resets it.
  //  1. re-issue play on this device at the frozen position (~1s; what fixed
  //     the Sep 3 and Sep 9 freezes when the user pressed play by hand)
  //  2. reconnect the player (~4s), auto-resume restores playback
  //  3. build a brand-new Player — a reconnect can keep a dead device id
  // Steps are spaced by a backoff, each still needs two unhealthy polls, and
  // after step 3 (or too many recoveries in a short time) we stop and leave
  // it to the user. "Unhealthy" in a car is not always a dead stream (a call,
  // Bluetooth dropping, a pause from the steering wheel) — fighting that is
  // worse than one extra tap on Play.
  private recoverPlayback(reason: string): void {
    this.expireStaleRecoveryEpisode();
    const now = Date.now();
    const nextStep = this.recoveryEpisodeStep + 1;
    // The budget counts only the heavy steps (2-3) — i.e. step 1 already
    // failed — and a user Play press clears it.
    this.recoveryTimestamps = this.recoveryTimestamps.filter(at => now - at < this.RECOVERY_BUDGET_WINDOW_MS);
    if (nextStep > this.MAX_RECOVERY_STEPS || (nextStep >= 2 && this.recoveryTimestamps.length >= this.RECOVERY_BUDGET)) {
      this.giveUpRecovery(reason);
      return;
    }
    const waitMs = this.RECOVERY_STEP_BACKOFF_MS[nextStep - 1] - (now - this.lastRecoveryStepAt);
    if (this.recoveryEpisodeStep > 0 && waitMs > 0) {
      console.log(`[health] ${reason}: waiting ${Math.ceil(waitMs / 1000)}s more before recovery step ${nextStep}`);
      this.noteHealthVerdict(`backoff-before-step-${nextStep}`);
      return;
    }
    this.recoveryEpisodeStep = nextStep;
    this.lastRecoveryStepAt = now;
    this.lastAutomaticActionAt = now;
    if (nextStep >= 2) this.recoveryTimestamps.push(now);
    this.currentRecoveryReason = reason;
    this.clearStallSuspect('recovered');
    this.sdkLoadingSince = null;
    if (nextStep === 1 && reason !== 'playback-error' && this.isReadySubject.value && this.deviceId) {
      console.log(`[health] recovering playback (${reason}) — step 1: restart on this device`);
      void this.restartOnSameDevice(reason);
      return;
    }
    if (nextStep <= 2) {
      console.log(`[health] recovering playback (${reason}) — step ${nextStep}: reconnect`);
      this.forceReconnect(`health-${reason}`);
      return;
    }
    console.log(`[health] recovering playback (${reason}) — step ${nextStep}: fresh player`);
    void this.recreatePlayer(`health-${reason}`);
  }

  // Stop trying. Intent goes to not-playing so nothing restarts on its own;
  // the user's next Play press starts fresh.
  private giveUpRecovery(reason: string): void {
    console.warn(`[health] automatic recovery stopped (${reason}) — waiting for the user to press Play`);
    this.noteHealthVerdict(`gave-up-${reason}`);
    this.cancelPendingAutoResume('recovery stopped');
    this.setIntendedPlaying(false);
    this.recoveryEpisodeStep = 0;
    this.currentRecoveryReason = null;
    this.resetHealthStrikes();
    this.clearStallSuspect('cleared');
    // A wedged SDK ignores player.togglePlay()/resume(): the next Play press
    // must restart through the Web API.
    this.needsHardRestart = true;
    this.playbackNoticeSubject.next('Playback stopped — tap Play to resume');
  }

  private async restartOnSameDevice(reason: string): Promise<void> {
    const deviceId = this.deviceId;
    // Loading-stuck: the server's progress is extrapolated past audio that
    // was never heard — restart from the SDK's own position.
    const snapshot = this.buildResumeSnapshot({ trustServerProgress: reason !== 'sdk-loading-stuck' });
    if (!deviceId || !snapshot) {
      console.log('[health] step 1: nothing known to restart — reconnecting instead');
      this.forceReconnect(`health-${reason}`);
      return;
    }
    this.recoveryInFlight = true;
    this.markPlaybackRestart(this.RESTART_GRACE_MS);
    try {
      await this.withTimeout(this.startPlaybackFromSnapshot(deviceId, snapshot), this.RESTART_COMMAND_TIMEOUT_MS, 'same-device restart');
      if (!this.intendedPlaying) {
        // The user paused while the restart was in flight — their pause wins.
        console.log('[health] step 1 landed after a user pause — pausing again');
        await this.player?.pause().catch(() => undefined);
        return;
      }
      console.log(`[health] step 1: playback re-issued at ${snapshot.positionMs}ms`);
    } catch (error) {
      // No direct escalation from here: the health check judges the result
      // and the ladder (with its backoff) decides what happens next.
      if (this.isDeviceNotFoundError(error)) {
        const listed = await this.isOurDeviceListed();
        if (listed === true) {
          // Our device looks fine, so Spotify may have rejected the context.
          // One fresh player is worth a try (the listing can lag); if that
          // fails too the ladder hits its ceiling and stops.
          console.warn('[health] step 1 got 404 but our device is listed — next step will try a fresh player once');
          this.recoveryEpisodeStep = Math.max(this.recoveryEpisodeStep, 2);
        } else {
          console.warn(`[health] step 1 got 404 and our device is ${listed === false ? 'gone' : 'unconfirmed'} — next step will build a fresh player`);
          this.recoveryEpisodeStep = Math.max(this.recoveryEpisodeStep, 2);
        }
      } else {
        console.warn('[health] step 1 restart did not complete (the health check will judge):', error);
      }
    } finally {
      this.recoveryInFlight = false;
      // The grace window starts when the command actually landed.
      this.markPlaybackRestart(this.RESTART_GRACE_MS);
    }
  }

  // A playback start we issued ourselves: buffering grace, fresh strike count,
  // and a new progress baseline (the playhead moved).
  private markPlaybackRestart(graceMs: number): void {
    this.lastAutomaticActionAt = Date.now();
    this.commandGraceUntil = Math.max(this.commandGraceUntil, Date.now() + graceMs);
    this.progressBaselineAt = Date.now();
    this.resetHealthStrikes();
    this.sdkLoadingSince = null;
  }

  // ---- Stall suspects (SDK side) ----
  // A buffer running dry shows up as ONE unprompted player_state_changed at
  // the frozen position, same track, not paused (nine field reports, all
  // frozen within 50ms of a 10s chunk boundary). Short rebuffers send a second
  // event within 2-6s and heal themselves; otherwise a quick poll confirms.
  private noteSdkStateForStalls(previousState: PlaybackState | null, state: PlaybackState | null): void {
    const now = Date.now();
    const unprompted = now >= this.commandGraceUntil && !this.isReconnecting;
    const trackId = state?.track_window?.current_track?.id ?? null;
    if (unprompted && state) {
      console.log(`[sdk] state paused=${state.paused} loading=${!!state.loading} pos=${Math.round(state.position)} track=${trackId ?? 'none'}`);
    }

    // An unprompted mid-track PAUSE is either a pause from outside the app
    // (steering-wheel/lock-screen button, a call, Bluetooth dropping) or a
    // dying stream — dying streams emit real pause events too (Sep reports).
    // Only respect it when it is CONFIDENTLY an outside pause; anything
    // ambiguous stays with the health check, which recovers. A wrong restart
    // costs one more pause press; a stall left stopped is the #1 complaint.
    // (While the bug-report hold is on, the mic causes this pause; release
    // handles it.)
    if (unprompted && state?.paused && previousState && !previousState.paused &&
        this.intendedPlaying && !this.recoveryHoldReason) {
      this.expireStaleRecoveryEpisode();
      const sameTrack = !!trackId && previousState.track_window?.current_track?.id === trackId;
      const midTrack = state.position > 1000 && state.position < state.duration - 3000;
      const lastVerdict = this.healthVerdicts[this.healthVerdicts.length - 1]?.verdict ?? '';
      // Every field stall froze within 50ms of a 10s audio-chunk boundary;
      // button presses land anywhere.
      const chunkOffsetMs = state.position % 10000;
      const atChunkBoundary = chunkOffsetMs < 150 || chunkOffsetMs > 9850;
      const stallEvidence = !!this.stallSuspect || this.healthStrikes > 0 ||
        lastVerdict.startsWith('unhealthy') || this.recoveryEpisodeStep > 0 ||
        atChunkBoundary ||
        !!state.loading || now - this.lastSdkLoadingAt < 5000 ||
        now - this.lastPlaybackErrorAt < 5000 ||
        now - this.lastAutomaticActionAt < this.OUTSIDE_PAUSE_QUIET_MS;
      if (!sameTrack || !midTrack || stallEvidence) {
        console.log(`[health] unprompted pause at ${Math.round(state.position)}ms not treated as an outside pause (${atChunkBoundary ? 'at a chunk boundary' : stallEvidence ? 'stall evidence' : 'not mid-track'}) — health check decides`);
      } else {
        console.log('[health] paused from outside the app (media button, call or audio focus) — respecting it');
        this.cancelPendingAutoResume('external pause');
        this.setIntendedPlaying(false);
        this.resetHealthStrikes();
        this.recoveryEpisodeStep = 0;
        this.externallyPaused = true;
      }
    }
    // ...and the matching resume from outside (play on the steering wheel)
    // puts the health check back on duty.
    if (unprompted && state && !state.paused && this.externallyPaused && !this.intendedPlaying &&
        !!trackId && previousState?.track_window?.current_track?.id === trackId) {
      console.log('[health] resumed from outside the app — watching playback again');
      this.externallyPaused = false;
      this.setIntendedPlaying(true);
      this.progressBaselineAt = now;
    }

    // Any later event resolves an open suspect (the resume after a rebuffer,
    // a pause, a track change). Don't re-arm on the resolving event itself.
    if (this.stallSuspect) {
      const resumed = !!state && !state.paused && trackId === this.stallSuspect.trackId;
      this.clearStallSuspect(resumed ? 'self-healed' : 'cleared');
      return;
    }

    if (!unprompted || !state || !previousState || !this.intendedPlaying || state.paused) return;
    if (!trackId || previousState.track_window?.current_track?.id !== trackId) return;
    if (!(state.position > 1000 && state.position < state.duration - 3000)) return;

    const episode: StallEpisode = { at: now, trackId, positionMs: Math.round(state.position), outcome: 'open', resolvedAfterMs: null };
    this.stallEpisodes.push(episode);
    if (this.stallEpisodes.length > this.FORENSIC_RING_SIZE) this.stallEpisodes.shift();
    this.stallSuspect = { at: now, trackId, positionMs: episode.positionMs, episode };
    console.log(`[stall] suspect: unprompted SDK event mid-track at ${episode.positionMs}ms — confirming in ${this.STALL_SUSPECT_CONFIRM_MS}ms`);
    this.stallSuspectTimer = setTimeout(() => {
      this.stallSuspectTimer = null;
      if (this.stallSuspect?.episode !== episode) return;
      console.log('[stall] suspect still open — polling to confirm');
      void this.pollCrossDevicePlayback();
    }, this.STALL_SUSPECT_CONFIRM_MS);
  }

  private clearStallSuspect(outcome: StallEpisode['outcome']): void {
    if (this.stallSuspectTimer) {
      clearTimeout(this.stallSuspectTimer);
      this.stallSuspectTimer = null;
    }
    const suspect = this.stallSuspect;
    if (!suspect) return;
    this.stallSuspect = null;
    suspect.episode.outcome = outcome;
    suspect.episode.resolvedAfterMs = Date.now() - suspect.at;
    if (outcome === 'self-healed') {
      console.log(`[stall] resolved on its own after ${suspect.episode.resolvedAfterMs}ms`);
    }
  }

  // The ONE SDK event allowed to clear intent: a pause at the natural end of
  // the queue (last track finished, no repeat). Without this, the health check
  // would "recover" silence the user didn't ask for. A mid-queue pause event
  // never clears intent — that is the field-observed stream-death shape.
  private noteNaturalPlaybackEnd(state: PlaybackState | null): void {
    // state is null when the SDK loses its device (e.g. mid-reconnect).
    if (!state || !this.intendedPlaying || !state.paused) return;
    if ((state.track_window?.next_tracks?.length ?? 0) > 0) return;
    const nearEnd = state.duration > 0 && state.position >= state.duration - 2000;
    const atStart = state.position === 0;
    if (nearEnd || atStart) {
      console.log('[health] queue ended naturally — intent set to not-playing');
      this.setIntendedPlaying(false);
      this.resetHealthStrikes();
    }
  }


  // Called at the top of every user-initiated playback command: records the
  // user's intent (the health check's single source of truth), performs the
  // one-time mobile autoplay unlock, and grants a short grace window so
  // recovery never fights the user's own action.
  private noteUserPlaybackCommand(command: string, intendPlaying: boolean): void {
    this.cancelPendingAutoResume('user command'); // an explicit user command always wins over auto-resume
    this.setIntendedPlaying(intendPlaying);
    this.activateElementOnce();
    this.commandGraceUntil = Date.now() + this.COMMAND_GRACE_MS;
    this.progressBaselineAt = Date.now();
    this.resetHealthStrikes();
    this.recoveryEpisodeStep = 0; // a user command starts a fresh episode
    if (command !== 'seek') this.externallyPaused = false; // a seek doesn't change play/pause intent
    if (intendPlaying && command !== 'seek') {
      this.recoveryTimestamps = []; // the user asked for music: recovery gets a fresh budget
    }
    if (PlaybackService.WEB_API_START_COMMANDS.has(command)) {
      this.needsHardRestart = false; // started through the Web API — the SDK stream is fresh
    }
    this.recoverOnFirstUnhealthy = false;
    this.sdkLoadingSince = null;
    this.clearStallSuspect('cleared');
    this.lastCommand = { command, issuedAt: Date.now(), echoedAt: null }; // forensic record only
  }

  // Forensic only: stamps when the SDK first acknowledged the last command
  // with a player_state_changed event. Echo latency in bug reports separates
  // "command landed" from "command swallowed by a dead stream".
  private noteCommandEchoed(): void {
    if (this.lastCommand && this.lastCommand.echoedAt === null) {
      this.lastCommand.echoedAt = Date.now();
    }
  }

  // Guard: disarms the pending snapshot AND cancels any auto-resume
  // already in flight (the retry loop checks the flag). Also covers commands
  // aimed at ANOTHER device — recovery must never yank playback off a device
  // the user explicitly chose.
  private cancelPendingAutoResume(reason: string): void {
    this.autoResumeCancelled = true;
    if (this.pendingAutoResume) {
      console.log(`[recovery] auto-resume disarmed: ${reason}`);
      this.pendingAutoResume = null;
    }
  }

  // Best-effort autoplay unlock for a Player built without a user tap. The
  // flag stays false so the next real gesture still runs activateElement().
  private tryActivateNewPlayerElement(): void {
    this.elementActivated = false;
    try {
      if (this.player && typeof this.player.activateElement === 'function') {
        Promise.resolve(this.player.activateElement()).catch(() => { /* no gesture yet — expected */ });
      }
    } catch {
      // non-fatal
    }
  }

  private activateElementOnce(): void {
    if (this.elementActivated || !this.player) return;
    this.elementActivated = true;
    try {
      if (typeof this.player.activateElement === 'function') {
        Promise.resolve(this.player.activateElement()).catch((error) => {
          console.warn('[recovery] activateElement rejected (non-fatal):', error);
        });
        console.log('[recovery] player.activateElement() called (mobile autoplay unlock)');
      }
    } catch (error) {
      console.warn('[recovery] activateElement failed (non-fatal):', error);
    }
  }

  // Playback control methods
  public async play(contextUri?: string, uris?: string[], offset?: number): Promise<void> {
    if (!this.isReadySubject.value || !this.deviceId) {
      throw new Error('Player not ready');
    }
    this.noteUserPlaybackCommand('play', true);
    this.rememberPlayRequest(contextUri ?? null, uris ?? null, offset, 0);

    try {
      // Use backend Spotify Connect API to start playback
      await this.spotifyService.startPlayback(
        this.deviceId,
        contextUri,
        uris,
        offset
      ).toPromise();
    } catch (error) {
      console.error('Failed to start playback via Connect API, falling back to SDK:', error);
      // Fallback to SDK resume method
      if (this.player) {
        await this.player.resume();
      }
    }
  }

  public async pause(): Promise<void> {
    if (this.player) {
      this.noteUserPlaybackCommand('pause', false);
      await this.player.pause();
    }
  }

  public async resume(): Promise<void> {
    if (this.needsHardRestart) {
      await this.hardRestartHere();
      return;
    }
    if (this.player) {
      this.noteUserPlaybackCommand('resume', true);
      await this.player.resume();
    }
  }

  // Pause whatever device is playing, through the Web API — and make sure
  // nothing of ours (intent, pending auto-resume) restarts it afterwards.
  public async pauseActiveDevice(): Promise<void> {
    this.noteUserPlaybackCommand('pause', false); // also disarms any pending auto-resume
    await this.spotifyService.pausePlayback().toPromise();
  }

  // After recovery gave up the SDK may be wedged: player.togglePlay() and
  // resume() would do nothing. Restart at the last known spot via the Web API.
  private async hardRestartHere(): Promise<void> {
    console.log('[play] automatic recovery had stopped — restarting playback through the Web API');
    const snapshot = this.buildResumeSnapshot(); // before any reconnect wipes the cached state
    if (!snapshot) {
      await this.takeOverPlayback();
      return;
    }
    const ready = await this.ensurePlayerReady('hard-restart');
    if (!ready || !this.deviceId) {
      throw new Error('Player not ready');
    }
    this.noteUserPlaybackCommand('play', true);
    this.lastPlayRequest = {
      contextUri: snapshot.contextUri,
      trackUris: snapshot.trackUris ?? null,
      offset: snapshot.trackUri ? { uri: snapshot.trackUri } : snapshot.offsetIndex,
      positionMs: snapshot.positionMs,
      at: Date.now()
    };
    try {
      await this.startPlaybackFromSnapshot(this.deviceId, snapshot);
    } catch (error) {
      if (!this.isDeviceNotFoundError(error) || await this.isOurDeviceListed() === true) throw error;
      console.warn('[play] restart got 404 — stale device id, building a fresh player and retrying once');
      const recovered = await this.recreatePlayer('hard-restart-404', { skipAutoResumeCapture: true });
      if (!recovered || !this.deviceId) throw error;
      await this.startPlaybackFromSnapshot(this.deviceId, snapshot);
    }
  }

  public async togglePlay(): Promise<void> {
    if (this.needsHardRestart) {
      await this.hardRestartHere();
      return;
    }
    const cached = this.playbackStateSubject.value;
    // No local playback loaded: player.togglePlay() would be a silent no-op
    // (Oct 13/15: pause buttons for a dead device, presses did nothing).
    // The user wants music — start it here.
    if (!this.player || !cached?.track_window?.current_track) {
      // No local state (other device, or just reconnected): if something is
      // audibly playing per Spotify, this press means PAUSE — never start
      // music here for it. Take over only when nothing (live) is playing.
      const remote = this.crossDeviceStateSubject.value;
      if (remote?.isPlaying && !remote.isStale) {
        console.log(`[toggle] no local playback; pausing ${remote.isWebSDKActive ? 'this device' : 'the playing device'} via the Web API`);
        await this.pauseActiveDevice();
        return;
      }
      await this.takeOverPlayback();
      return;
    }
    // Intent from the cached state: paused -> the user wants playback;
    // recovery must restore audio, not leave silence.
    this.noteUserPlaybackCommand('toggle', cached.paused);
    await this.player.togglePlay();
  }

  public async nextTrack(): Promise<void> {
    if (this.player) {
      this.noteUserPlaybackCommand('next', true);
      await this.player.nextTrack();
    }
  }

  public async previousTrack(): Promise<void> {
    if (this.player) {
      this.noteUserPlaybackCommand('previous', true);
      await this.player.previousTrack();
    }
  }

  public async seek(positionMs: number): Promise<void> {
    if (this.player) {
      this.noteUserPlaybackCommand('seek', this.intendedPlaying); // seeking does not change play/pause intent
      await this.player.seek(positionMs);
    }
  }

  public async setVolume(volume: number): Promise<void> {
    if (this.player) {
      await this.player.setVolume(Math.max(0, Math.min(1, volume)));
    }
  }

  public async getVolume(): Promise<number> {
    if (this.player) {
      return await this.player.getVolume();
    }
    return 0;
  }

  public async getCurrentState(): Promise<PlaybackState | null> {
    if (this.player) {
      return await this.player.getCurrentState();
    }
    return null;
  }

  // Live getCurrentState probe for bug-report capture — bounded by
  // timeoutMs and NEVER rejects, so callers may await it bare.
  public async probeLiveState(timeoutMs = 3000): Promise<LivePlayerProbeResult> {
    if (!this.player) { return { outcome: 'no-player', state: null, elapsedMs: 0 }; }
    const startedAt = Date.now();
    try {
      const state = await this.withTimeout(this.player.getCurrentState(), timeoutMs, 'bug-report live state probe');
      return { outcome: state ? 'state' : 'null-state', state, elapsedMs: Date.now() - startedAt };
    } catch {
      return { outcome: 'timeout-or-error', state: null, elapsedMs: Date.now() - startedAt };
    }
  }

  // Synchronous forensic snapshot for bug-report capture.
  public getRecoveryDiagnostics(): PlaybackRecoveryDiagnostics {
    return {
      lastPlayerStateChangedAt: this.lastPlayerStateChangedAt,
      lastHealthCheckAt: this.lastHealthCheckAt,
      lastSuccessfulPollAt: this.lastSuccessfulPollAt,
      lastPolledPlayerState: this.lastPolledPlayerState ? { ...this.lastPolledPlayerState } : null,
      lastCommand: this.lastCommand ? { ...this.lastCommand } : null,
      intendedPlaying: this.intendedPlaying,
      healthStrikes: this.healthStrikes,
      recoveryHold: this.recoveryHoldReason,
      healthVerdicts: this.healthVerdicts.map(v => ({ ...v })),
      pollHistory: this.pollHistory.map(pollEntry => ({ ...pollEntry })),
      stallEpisodes: this.stallEpisodes.map(episode => ({ ...episode }))
    };
  }

  // Utility methods
  // True when the last poll saw another device claim playback while frozen or
  // parked at the end of its track — i.e. a dead device.
  public isRemotePlaybackStale(): boolean {
    return this.crossDeviceStateSubject.value?.isStale === true;
  }

  public getDeviceId(): string | null {
    return this.deviceId;
  }

  public isPlayerReady(): boolean {
    return this.isReadySubject.value;
  }

  public getCurrentTrack(): Observable<any> {
    return this.playbackState$.pipe(
      filter(state => state !== null),
      map(state => state?.track_window?.current_track || null)
    );
  }

  public getIsPlaying(): Observable<boolean> {
    return this.playbackState$.pipe(
      map(state => state ? !state.paused : false)
    );
  }

  public getProgress(): Observable<number> {
    return this.playbackState$.pipe(
      map(state => state ? state.position : 0)
    );
  }

  public getDuration(): Observable<number> {
    return this.playbackState$.pipe(
      map(state => state ? state.duration : 0)
    );
  }

  public getShuffleState(): Observable<boolean> {
    return this.playbackState$.pipe(
      map(state => state ? state.shuffle : false)
    );
  }

  public getRepeatMode(): Observable<number> {
    return this.playbackState$.pipe(
      map(state => state ? state.repeat_mode : 0)
    );
  }

  // Format duration helper
  public formatDuration(durationMs: number): string {
    if (!Number.isFinite(durationMs) || durationMs < 0) return '0:00';
    const minutes = Math.floor(durationMs / 60000);
    const seconds = Math.floor((durationMs % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  // Format progress helper
  public formatProgress(progressMs: number, durationMs: number): string {
    const progress = this.formatDuration(progressMs);
    const duration = this.formatDuration(durationMs);
    return `${progress} / ${duration}`;
  }

  // Playlist playback methods
  public async playPlaylist(playlistUri: string, trackOffset?: number, positionMs?: number): Promise<void> {
    console.log('PlaybackService.playPlaylist called with:', { playlistUri, trackOffset, positionMs });
    console.log('Player ready:', this.isReadySubject.value);
    console.log('Device ID:', this.deviceId);

    if (!this.isReadySubject.value || !this.deviceId) {
      const error = `Player not ready - Ready: ${this.isReadySubject.value}, Device ID: ${this.deviceId}`;
      console.error(error);
      throw new Error(error);
    }

    this.noteUserPlaybackCommand('play-playlist', true);
    this.rememberPlayRequest(playlistUri, null, trackOffset, positionMs ?? 0);

    try {
      await this.startPlaylistOnLocalDevice(playlistUri, trackOffset, positionMs);
    } catch (error) {
      // Spotify no longer knows our device id — happens after a silent SDK
      // death (Jul 11 "continue playing x3" report: every retry 404ed the same
      // way). A reconnect of the same Player can hang waiting for a 'ready'
      // that never comes (Oct 9 report), so build a fresh Player, then retry
      // the whole sequence once.
      if (this.isDeviceNotFoundError(error) && await this.isOurDeviceListed() === true) {
        // Spotify also answers 404 for a context it won't play — our device is fine.
        console.warn('[recovery] play got 404 but our device is registered — the playlist itself was rejected');
        throw error;
      }
      if (this.isDeviceNotFoundError(error)) {
        console.warn('[recovery] transfer/play got 404 — stale SDK device id, building a fresh player and retrying once');
        this.cancelPendingAutoResume('stale-device retry'); // our retry IS the resume; auto-resume must not race it
        const recovered = await this.recreatePlayer('stale-device-404', { skipAutoResumeCapture: true });
        if (recovered && this.deviceId) {
          try {
            await this.startPlaylistOnLocalDevice(playlistUri, trackOffset, positionMs);
          } catch (retryError) {
            // Oct 22: three brand-new devices in a row were 'ready' yet 404ed — record what Spotify listed
            if (this.isDeviceNotFoundError(retryError)) {
              console.warn('[recovery] fresh device also got 404');
              await this.isOurDeviceListed();
            }
            throw retryError;
          }
          return;
        }
      }
      console.error('Failed to play playlist via SpotifyService:', error);
      throw error;
    }
  }

  private async startPlaylistOnLocalDevice(playlistUri: string, trackOffset?: number, positionMs?: number): Promise<void> {
    // First, make sure this device is active. play=false: transferring with
    // play=true briefly started the OLD context here and logged a bogus play
    // (Oct 20 report) — startPlayback below starts the right thing.
    console.log('Transferring playback to this device...');
    await this.transferPlaybackHere(false);

    // Small delay to ensure transfer is complete
    await new Promise(resolve => setTimeout(resolve, 500));

    const deviceId = this.deviceId;
    if (!deviceId) {
      throw new Error('Device lost while starting playback');
    }
    console.log('Calling SpotifyService.startPlayback with:', {
      deviceId,
      contextUri: playlistUri,
      uris: undefined,
      offset: trackOffset,
      positionMs
    });

    const result = await this.spotifyService.startPlayback(
      deviceId,
      playlistUri,
      undefined,
      trackOffset,
      positionMs
    ).toPromise();

    console.log('SpotifyService.startPlayback result:', result);
  }

  private rememberPlayRequest(contextUri: string | null, trackUris: string[] | null, offset: number | { uri: string } | undefined, positionMs: number): void {
    if (!contextUri && !trackUris?.length) return;
    this.lastPlayRequest = { contextUri, trackUris, offset, positionMs, at: Date.now() };
  }

  private isDeviceNotFoundError(error: unknown): boolean {
    return !!error && typeof error === 'object' && (error as { status?: unknown }).status === 404;
  }

  public async playTracks(trackUris: string[], offset?: number): Promise<void> {
    if (!this.isReadySubject.value || !this.deviceId) {
      throw new Error('Player not ready');
    }
    this.noteUserPlaybackCommand('play-tracks', true);
    this.rememberPlayRequest(null, trackUris, offset, 0);

    try {
      await this.spotifyService.startPlayback(
        this.deviceId,
        undefined,
        trackUris,
        offset
      ).toPromise();
    } catch (error) {
      console.error('Failed to play tracks:', error);
      throw error;
    }
  }

  public async playAlbum(albumUri: string, trackOffset?: number): Promise<void> {
    if (!this.isReadySubject.value || !this.deviceId) {
      throw new Error('Player not ready');
    }
    this.noteUserPlaybackCommand('play-album', true);
    this.rememberPlayRequest(albumUri, null, trackOffset, 0);

    try {
      await this.spotifyService.startPlayback(
        this.deviceId,
        albumUri,
        undefined,
        trackOffset
      ).toPromise();
    } catch (error) {
      console.error('Failed to play album:', error);
      throw error;
    }
  }

  // Shuffle and repeat controls
  public async toggleShuffle(): Promise<void> {
    if (!this.isReadySubject.value || !this.deviceId) {
      throw new Error('Player not ready');
    }

    try {
      const currentState = this.playbackStateSubject.value;
      const newShuffleState = !currentState?.shuffle;
      
      await this.spotifyService.setShuffle(newShuffleState, this.deviceId).toPromise();
    } catch (error) {
      console.error('Failed to toggle shuffle:', error);
      throw error;
    }
  }

  public async setShuffle(shuffleState: boolean): Promise<void> {
    if (!this.isReadySubject.value || !this.deviceId) {
      throw new Error('Player not ready');
    }

    try {
      await this.spotifyService.setShuffle(shuffleState, this.deviceId).toPromise();
    } catch (error) {
      console.error('Failed to set shuffle:', error);
      throw error;
    }
  }

  public async cycleRepeatMode(): Promise<void> {
    if (!this.isReadySubject.value || !this.deviceId) {
      throw new Error('Player not ready');
    }

    try {
      const currentState = this.playbackStateSubject.value;
      let newRepeatMode: 'off' | 'context' | 'track';

      switch (currentState?.repeat_mode) {
        case 0: // off
          newRepeatMode = 'context';
          break;
        case 2: // context
          newRepeatMode = 'track';
          break;
        case 1: // track
        default:
          newRepeatMode = 'off';
          break;
      }

      await this.spotifyService.setRepeatMode(newRepeatMode, this.deviceId).toPromise();
    } catch (error) {
      console.error('Failed to cycle repeat mode:', error);
      throw error;
    }
  }

  public async setRepeatMode(repeatMode: 'off' | 'context' | 'track'): Promise<void> {
    if (!this.isReadySubject.value || !this.deviceId) {
      throw new Error('Player not ready');
    }

    try {
      await this.spotifyService.setRepeatMode(repeatMode, this.deviceId).toPromise();
    } catch (error) {
      console.error('Failed to set repeat mode:', error);
      throw error;
    }
  }

  // Queue management methods
  public async addToQueue(trackUri: string): Promise<void> {
    if (!this.isReadySubject.value || !this.deviceId) {
      throw new Error('Player not ready');
    }

    try {
      // This would need to be implemented in the backend Spotify service
      // For now, we'll log the attempt
      console.log('Add to queue:', trackUri);
      // await this.spotifyService.addToQueue(trackUri, this.deviceId).toPromise();
    } catch (error) {
      console.error('Failed to add to queue:', error);
      throw error;
    }
  }

  // Transfer playback to this device
  public async transferPlaybackHere(play: boolean = false): Promise<void> {
    if (!this.deviceId) {
      throw new Error('Device not ready');
    }

    try {
      await this.spotifyService.transferPlayback([this.deviceId], play).toPromise();
    } catch (error) {
      console.error('Failed to transfer playback:', error);
      throw error;
    }
  }

  // Context helpers
  public getCurrentContext(): Observable<any> {
    return this.playbackState$.pipe(
      map(state => state?.context || null)
    );
  }

  public getContextType(): Observable<string | null> {
    return this.getCurrentContext().pipe(
      map(context => {
        if (!context?.uri) return null;
        return context.uri.split(':')[1] || null;
      })
    );
  }

  // Enhanced progress tracking with intervals
  public startProgressTracking(): void {
    // This could be enhanced to update progress more frequently when playing
    // For now, the Web Playback SDK handles this automatically
  }

  public stopProgressTracking(): void {
    // Cleanup for progress tracking if needed
  }

  // Public method to refresh premium status
  public async refreshPremiumStatus(): Promise<boolean> {
    await this.checkPremiumStatus();
    return this.isPremiumSubject.value;
  }

  // Get current premium status without making API call
  public isPremiumUser(): boolean {
    return this.isPremiumSubject.value;
  }

  // Play tracking methods
  private handlePlaybackStateChange(state: PlaybackState | null): void {
    if (!state || !state.track_window?.current_track) {
      return;
    }

    const currentTrack = state.track_window.current_track;
    const currentTrackId = currentTrack.id;
    const isPlaying = !state.paused;
    const currentPosition = state.position;

    // Track changed - record the PREVIOUS track with its progress, then store the new track
    if (this.lastTrackedTrack !== currentTrackId) {
      // Record the previous track if we have data for it
      // Use the lastKnownPosition we captured BEFORE this state change
      if (this.lastTrackedTrackData && this.lastKnownPosition > 0) {
        console.log(`🎵 Recording previous track: "${this.lastTrackedTrackData.name}" - played ${this.lastKnownPosition}ms of ${this.lastTrackedTrackData.durationMs}ms`);
        this.recordTrackWithProgress(this.lastTrackedTrackData, this.lastKnownPosition);
      }

      // Store the new track data for when IT ends later
      if (isPlaying) {
        console.log(`🎶 New track started: "${currentTrack.name}" by ${currentTrack.artists[0]?.name}`);
        this.lastTrackedTrack = currentTrackId;
        this.lastKnownPosition = currentPosition; // Reset to new track's position
        this.storeCurrentTrackData(state);
      }
    } else {
      // Same track - update the position only when track hasn't changed
      if (isPlaying) {
        this.lastKnownPosition = currentPosition;
      }
    }

    // Track resume-position regardless of track change. We only care when the
    // context is a playlist — Continue Playing only resumes from playlist contexts.
    const contextUri = state.context?.uri;
    if (contextUri && contextUri.startsWith('spotify:playlist:')) {
      const playlistId = contextUri.slice('spotify:playlist:'.length);
      this.updateLatestPosition(
        playlistId,
        currentTrackId,
        currentTrack.uri,
        currentPosition,
        currentTrack.duration_ms
      );
      // Flush immediately on pause so closing the tab while paused doesn't lose it.
      if (!isPlaying) {
        this.flushPosition();
      }
    }
  }

  private storeCurrentTrackData(state: PlaybackState): void {
    const track = state.track_window.current_track;
    const context = state.context;

    // Determine context type and URI
    let contextType: 'playlist' | 'album' | 'artist' | 'track' | undefined;
    let contextUri: string | undefined;
    let playlistId: string | undefined;

    if (context?.uri) {
      const uriParts = context.uri.split(':');
      if (uriParts.length >= 2) {
        const type = uriParts[1];
        if (type === 'playlist' || type === 'album' || type === 'artist') {
          contextType = type as 'playlist' | 'album' | 'artist';
          contextUri = context.uri;
          if (type === 'playlist' && uriParts.length >= 3) {
            playlistId = uriParts[2];
          }
        }
      }
    }

    const albumImage = track.album.images?.[1]?.url || track.album.images?.[0]?.url;

    this.lastTrackedTrackData = {
      id: track.id,
      uri: track.uri,
      name: track.name,
      artistNames: track.artists.map(artist => artist.name),
      albumName: track.album.name,
      durationMs: track.duration_ms,
      contextType,
      contextUri,
      playlistId,
      albumImage
    };

    // Surface the current playlist context so other components (e.g. floating
    // player) can decide whether to show "Regenerate" affordances tied to that
    // playlist. null when not playing from a playlist.
    const nextContextId = contextType === 'playlist' ? (playlistId ?? null) : null;
    if (this.currentPlaylistContextIdSubject.value !== nextContextId) {
      this.currentPlaylistContextIdSubject.next(nextContextId);
    }
  }

  private recordTrackWithProgress(
    trackData: NonNullable<typeof this.lastTrackedTrackData>,
    progressMs: number
  ): void {
    this.playHistoryService.recordPlay(
      trackData.id,
      trackData.uri,
      trackData.name,
      trackData.artistNames,
      trackData.albumName,
      trackData.durationMs,
      trackData.contextType,
      trackData.contextUri,
      'user',
      trackData.playlistId,
      trackData.albumImage,
      progressMs
    );

    const percentPlayed = Math.round((progressMs / trackData.durationMs) * 100);
    console.log(`🎵 Recorded: "${trackData.name}" - ${percentPlayed}% played${trackData.playlistId ? ` (playlist: ${trackData.playlistId})` : ''}`);
  }


  // Public method to manually record a play (for when tracks are started via API)
  public recordTrackPlay(
    trackId: string,
    trackUri: string,
    trackName: string,
    artistNames: string[],
    albumName: string,
    durationMs: number,
    contextType?: 'playlist' | 'album' | 'artist' | 'track',
    contextUri?: string,
    playlistId?: string,
    albumImage?: string,
    progressMs?: number
  ): void {
    this.playHistoryService.recordPlay(
      trackId,
      trackUri,
      trackName,
      artistNames,
      albumName,
      durationMs,
      contextType,
      contextUri,
      'user',
      playlistId,
      albumImage,
      progressMs
    );
  }

  // Get play history service for external access
  public getPlayHistoryService(): PlayHistoryService {
    return this.playHistoryService;
  }

  // Read the saved resume position for a specific playlist (one round-trip per call).
  public async getSavedPlaybackPosition(playlistId: string): Promise<PlaybackPosition | null> {
    try {
      const positions = await this.backendStorage.getPlaybackPositions().toPromise();
      return positions?.[playlistId] || null;
    } catch (error) {
      console.warn('Failed to load saved playback position:', error);
      return null;
    }
  }

  // Device Management Methods
  public async getAvailableDevices(): Promise<DevicesResponse> {
    try {
      return await this.spotifyService.getAvailableDevices().toPromise() as DevicesResponse;
    } catch (error) {
      console.error('Failed to get available devices:', error);
      throw error;
    }
  }

  public async transferPlayback(deviceId: string, play: boolean = false): Promise<void> {
    this.cancelPendingAutoResume('playback transferred to another device');
    if (deviceId !== this.deviceId) this.setIntendedPlaying(false); // music intentionally moved off this device
    try {
      await this.spotifyService.transferPlayback([deviceId], play).toPromise();
    } catch (error) {
      console.error('Failed to transfer playback:', error);
      throw error;
    }
  }

  public async playPlaylistRemote(deviceId: string, playlistUri: string, trackOffset?: number, positionMs?: number): Promise<void> {
    this.cancelPendingAutoResume('playback started on another device');
    this.setIntendedPlaying(false); // music intentionally on another device
    await this.spotifyService.startPlayback(deviceId, playlistUri, undefined, trackOffset, positionMs).toPromise();
  }

  public async playTracksRemote(deviceId: string, trackUris: string[], offset?: number): Promise<void> {
    this.cancelPendingAutoResume('playback started on another device');
    this.setIntendedPlaying(false); // music intentionally on another device
    await this.spotifyService.startPlayback(deviceId, undefined, trackUris, offset).toPromise();
  }

  public async getActiveDeviceId(): Promise<string | null> {
    try {
      const response = await this.getAvailableDevices();
      const active = response.devices.find(d => d.is_active);
      return active?.id || null;
    } catch {
      return null;
    }
  }

  public async getCurrentActiveDevice(): Promise<SpotifyDevice | null> {
    try {
      const response = await this.getAvailableDevices();
      return response.devices.find(device => device.is_active) || null;
    } catch (error) {
      console.error('Failed to get current active device:', error);
      return null;
    }
  }

  // Cross-device polling methods
  public startCrossDevicePolling(): void {
    if (this.crossDevicePollingInterval) {
      return; // Already polling
    }

    console.log('Starting cross-device playback polling');

    // Poll immediately
    this.pollCrossDevicePlayback();

    // Set up interval - 5s when SDK not ready, 10s when SDK is active
    this.updatePollingInterval();
  }

  public stopCrossDevicePolling(): void {
    if (this.crossDevicePollingInterval) {
      clearInterval(this.crossDevicePollingInterval);
      this.crossDevicePollingInterval = null;
      this.currentPollIntervalMs = 0;
      console.log('Stopped cross-device playback polling');
    }

    if (this.errorRetryTimeout) {
      clearTimeout(this.errorRetryTimeout);
      this.errorRetryTimeout = null;
    }
  }

  private updatePollingInterval(): void {
    if (this.crossDevicePollingInterval) {
      clearInterval(this.crossDevicePollingInterval);
    }

    const interval = this.desiredPollIntervalMs();
    this.currentPollIntervalMs = interval;

    this.crossDevicePollingInterval = setInterval(() => {
      this.pollCrossDevicePlayback();
    }, interval);

    console.log(`Cross-device polling interval set to ${interval}ms (SDK ready: ${this.isReadySubject.value}, intent playing: ${this.intendedPlaying})`);
  }

  // The poll is the health check's clock: 5s whenever music should be playing
  // here (it used to drop to 10s, and always restarted at 10s after a network
  // blip — exactly when freezes happen). 10s only when idle with the SDK ready.
  private desiredPollIntervalMs(): number {
    return this.isReadySubject.value && !this.intendedPlaying ? 10000 : 5000;
  }

  private syncPollingInterval(): void {
    if (!this.crossDevicePollingInterval) return; // not polling (stopped / error backoff)
    if (this.desiredPollIntervalMs() !== this.currentPollIntervalMs) {
      this.updatePollingInterval();
    }
  }

  private setIntendedPlaying(value: boolean): void {
    if (this.intendedPlaying === value) return;
    this.intendedPlaying = value;
    this.syncPollingInterval();
  }

  // Every successful poll flows through here: shifts the comparison window,
  // records forensics, and runs the health check exactly once per poll.
  private recordPollResult(snapshot: PolledPlayerSnapshot): void {
    this.previousHealthPoll = this.lastPolledPlayerState;
    this.lastPolledPlayerState = snapshot;
    this.pollHistory.push({ ...snapshot });
    if (this.pollHistory.length > this.FORENSIC_RING_SIZE) {
      this.pollHistory.shift();
    }
    this.evaluatePlaybackHealth();
  }

  private async pollCrossDevicePlayback(): Promise<void> {
    const requestedAt = Date.now();
    // Bound the fetch so a stalled connection can't wedge the polling loop.
    const abortController = new AbortController();
    const abortTimer = setTimeout(() => abortController.abort(), 8000);
    try {
      const response = await fetch(`${environment.apiUrl}/spotify/player`, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        signal: abortController.signal
      });

      if (response.ok) {
        const playbackState = await response.json();

        if (playbackState && playbackState.item) {
          const track = playbackState.item;

          // Convert Spotify API track format to WebPlaybackTrack format
          const webPlaybackTrack: WebPlaybackTrack = {
            id: track.id,
            uri: track.uri,
            name: track.name,
            duration_ms: track.duration_ms,
            artists: track.artists.map((artist: any) => ({
              name: artist.name,
              uri: artist.uri
            })),
            album: {
              name: track.album.name,
              uri: track.album.uri,
              images: track.album.images || []
            }
          };

          // Check if the active device is our Web SDK
          const activeDeviceId: string | null = playbackState.device?.id ?? null;
          const isWebSDKActive = !!activeDeviceId && activeDeviceId === this.deviceId;
          const progressMs: number = playbackState.progress_ms || 0;
          // One of OUR earlier device ids (before a reconnect/recreate) that
          // Spotify still lists as active: silent, and still "us".
          const isRetiredOwn = !!activeDeviceId && !isWebSDKActive && this.retiredDeviceIds.has(activeDeviceId);
          const isStale = isRetiredOwn ||
            this.detectStaleRemotePlayback(playbackState.is_playing === true, activeDeviceId, isWebSDKActive, track.id, progressMs, track.duration_ms);
          const isPlaying = playbackState.is_playing === true && !isStale;

          this.lastPolledContext = {
            contextUri: typeof playbackState.context?.uri === 'string' ? playbackState.context.uri : null,
            trackUri: track.uri,
            progressMs,
            durationMs: track.duration_ms
          };

          const crossDeviceState: CrossDevicePlaybackState = {
            currentTrack: webPlaybackTrack,
            isPlaying,
            progress: progressMs,
            duration: track.duration_ms,
            isWebSDKActive,
            shuffleState: playbackState.shuffle_state || false,
            repeatMode: this.convertRepeatState(playbackState.repeat_state),
            isStale
          };

          this.crossDeviceStateSubject.next(crossDeviceState);

          // Surface playlist context from polled state so floating player can react
          let polledPlaylistId: string | null = null;
          const polledContextUri = playbackState.context?.uri;
          if (polledContextUri && typeof polledContextUri === 'string') {
            const parts = polledContextUri.split(':');
            if (parts.length >= 3 && parts[1] === 'playlist') {
              polledPlaylistId = parts[2];
            }
          }
          if (this.currentPlaylistContextIdSubject.value !== polledPlaylistId) {
            this.currentPlaylistContextIdSubject.next(polledPlaylistId);
          }

          // Track resume-position from polled state so playback on other devices
          // (or when SDK is not the active device) still gets persisted.
          if (polledPlaylistId) {
            const wasPlaying = this.latestPosition?.playlistId === polledPlaylistId
              && this.latestPosition?.trackId === track.id;
            this.updateLatestPosition(
              polledPlaylistId,
              track.id,
              track.uri,
              playbackState.progress_ms || 0,
              track.duration_ms
            );
            // If polled state shows paused, flush right away.
            if (!playbackState.is_playing && wasPlaying) {
              this.flushPosition();
            }
          }

          // Reset error count on success
          this.consecutiveErrors = 0;
          this.lastSuccessfulPollAt = Date.now(); // forensic timestamp
          this.recordPollResult({
            at: Date.now(),
            requestedAt,
            isPlaying,
            progressMs,
            trackId: track.id ?? null,
            deviceId: activeDeviceId,
            // A retired id of ours is never "another device" for the health check.
            isWebSDKActive: isWebSDKActive || isRetiredOwn
          });

          this.syncPollingInterval();
        } else {
          // No current track in playback state — "nothing playing anywhere" is
          // itself an unhealthy signal while intent is playing. The active
          // device is unknown here, so attribute it to our SDK device.
          this.crossDeviceStateSubject.next(null);
          this.consecutiveErrors = 0;
          this.lastSuccessfulPollAt = Date.now(); // forensic timestamp
          this.recordPollResult({ at: Date.now(), requestedAt, isPlaying: false, progressMs: 0, trackId: null, deviceId: null, isWebSDKActive: true });
        }
      } else if (response.status === 204) {
        // No active playback
        this.crossDeviceStateSubject.next(null);
        this.consecutiveErrors = 0;
        this.lastSuccessfulPollAt = Date.now(); // forensic timestamp
        this.recordPollResult({ at: Date.now(), requestedAt, isPlaying: false, progressMs: 0, trackId: null, deviceId: null, isWebSDKActive: true });
      } else if (response.status === 401) {
        // 401 may be genuine (re-auth needed) — verify before giving up.
        // Never a permanent dead-end: see handlePollingAuthError.
        this.crossDeviceStateSubject.next(null);
        this.handlePollingAuthError();
      } else {
        // 503 (transient token-refresh failure) and other errors: transient
        // path with backoff — polling is never permanently disabled.
        this.handlePollingError();
      }
    } catch (error) {
      // Failed polls carry no evidence — they never count as health strikes.
      console.warn('Error polling cross-device playback:', error);
      this.handlePollingError();
    } finally {
      clearTimeout(abortTimer);
    }
  }

  // Spotify keeps reporting is_playing for a device that died mid-stream
  // (yesterday's phone tab): progress frozen across polls, or parked at the
  // very end of the track. Such a device can't be heard — treat it as stopped
  // so the UI offers Play, and play presses take over instead of toggling a
  // device that will never answer (Oct 13/15 reports).
  private detectStaleRemotePlayback(
    isPlaying: boolean, deviceId: string | null, isWebSDKActive: boolean,
    trackId: string, progressMs: number, durationMs: number
  ): boolean {
    if (!isPlaying || !deviceId || isWebSDKActive) {
      this.lastRemoteSample = null;
      return false;
    }
    const now = Date.now();
    const previous = this.lastRemoteSample;
    const sameSample = !!previous && previous.deviceId === deviceId && previous.trackId === trackId && previous.progressMs === progressMs;
    const parkedAtEnd = durationMs > 0 && progressMs >= durationMs - 1000;
    const frozen = sameSample && now - previous!.at >= 4000;
    // Keep the FIRST sample of a frozen run so 'frozen' stays true on later polls.
    if (!sameSample) {
      this.lastRemoteSample = { deviceId, trackId, progressMs, at: now };
    }
    const isStale = parkedAtEnd || frozen;
    if (isStale && !(previous?.deviceId === deviceId && previous.trackId === trackId && this.staleRemoteLogged)) {
      console.log(`[poll] device ${deviceId} reports playing but is ${parkedAtEnd ? 'parked at the end of the track' : `frozen at ${progressMs}ms`} — treating it as stopped`);
    }
    this.staleRemoteLogged = isStale;
    return isStale;
  }

  // A 401 from the poll is often a transient refresh failure misread as an
  // auth problem. Re-check auth: if still authenticated, keep polling; if
  // genuinely logged out, pause but auto-rearm the moment authStatus$ flips
  // back to true. No state here can permanently stop polling.
  private handlePollingAuthError(): void {
    if (this.pollingAuthRecheckInFlight) return;
    this.pollingAuthRecheckInFlight = true;
    console.log('[recovery] poll got 401, re-checking auth status');
    this.authService.checkAuthStatus().subscribe({
      next: (isAuthenticated) => {
        this.pollingAuthRecheckInFlight = false;
        if (isAuthenticated) {
          console.log('[recovery] auth re-check OK, polling continues');
        } else {
          console.log('[recovery] genuinely unauthenticated, pausing polling until re-auth');
          this.stopCrossDevicePolling();
          this.armPollingOnReauth();
        }
      },
      error: (error) => {
        this.pollingAuthRecheckInFlight = false;
        console.warn('[recovery] auth re-check failed (transient), keeping polling with backoff:', error);
        this.handlePollingError();
      }
    });
  }

  private armPollingOnReauth(): void {
    if (this.pollingAuthRearmSubscription) return;
    this.pollingAuthRearmSubscription = this.authService.authStatus$
      .pipe(filter(isAuthenticated => isAuthenticated), take(1))
      .subscribe(() => {
        this.pollingAuthRearmSubscription = null;
        console.log('[recovery] auth restored, resuming cross-device polling');
        this.consecutiveErrors = 0;
        this.startCrossDevicePolling();
      });
  }

  // Immediate poll on connectivity events; also cuts an error-backoff pause
  // short — a restored network is exactly when a retry should happen.
  private triggerImmediatePoll(reason: string): void {
    // Connectivity events (online / visible / connection-change) often fire in
    // bursts within the same second — coalesce them into one poll so a burst
    // can't fan out duplicate fetches or drain the consecutive-error budget.
    if (Date.now() - this.lastImmediatePollAt < 2000) return;
    this.lastImmediatePollAt = Date.now();
    if (this.errorRetryTimeout) {
      clearTimeout(this.errorRetryTimeout);
      this.errorRetryTimeout = null;
      this.consecutiveErrors = 0;
      console.log(`[recovery] cutting poll error backoff short (${reason})`);
      this.startCrossDevicePolling();
      return;
    }
    if (!this.crossDevicePollingInterval) return; // polling not active (e.g. logged out)
    console.log(`[recovery] immediate cross-device poll (${reason})`);
    this.pollCrossDevicePlayback();
  }

  private handlePollingError(): void {
    this.consecutiveErrors++;

    if (this.consecutiveErrors >= this.maxConsecutiveErrors) {
      // Jittered 10-30s pause so many clients don't retry in lockstep.
      const retryDelay = 10000 + Math.floor(Math.random() * 20000);
      console.warn(`[recovery] pausing polling after ${this.consecutiveErrors} consecutive errors, will retry in ${Math.round(retryDelay / 1000)}s`);
      this.stopCrossDevicePolling();

      this.errorRetryTimeout = setTimeout(() => {
        this.errorRetryTimeout = null;
        this.consecutiveErrors = 0;
        this.startCrossDevicePolling();
      }, retryDelay);
    }
  }

  private convertRepeatState(repeatState: string | undefined): number {
    switch (repeatState) {
      case 'track': return 1;
      case 'context': return 2;
      default: return 0; // 'off' or undefined
    }
  }

  // Get combined state for a specific moment (useful for components that need current values)
  public getCombinedPlaybackState(): CrossDevicePlaybackState | null {
    const sdkState = this.playbackStateSubject.value;
    const polledState = this.crossDeviceStateSubject.value;
    const isSDKReady = this.isReadySubject.value;

    // If SDK is ready and has state, prefer it
    if (isSDKReady && sdkState && sdkState.track_window?.current_track) {
      return {
        currentTrack: sdkState.track_window.current_track,
        isPlaying: !sdkState.paused,
        progress: sdkState.position,
        duration: sdkState.duration,
        isWebSDKActive: polledState?.isWebSDKActive ?? true,
        shuffleState: sdkState.shuffle,
        repeatMode: sdkState.repeat_mode
      };
    }

    return polledState;
  }
}